import React, { useState, useEffect, useRef, useMemo } from 'react';
import { groupCRMUsersByDTC, sortCRMUsersWithDTCPriority } from '../../utils/crmUserSorting';
import {
  Box,
  Container,
  Heading,
  VStack,
  Button,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useToast,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  Select as ChakraSelect,
  Text,
  Divider,
  HStack,
  Badge,
  FormControl,
  FormLabel,
  Wrap,
  WrapItem,
  Tag,
  TagLabel,
  TagCloseButton,
  Checkbox,
  IconButton,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Grid,
  GridItem,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useColorModeValue,
  Spinner,
  Center,
  Icon,
  Progress,
  Tooltip,
  Kbd,
  Fade,
  ScaleFade,
  Link,
  Alert,
  AlertIcon,
  AlertDescription,
  Switch,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  SimpleGrid,
} from '@chakra-ui/react';
import { 
  FiArrowLeft, 
  FiUsers, 
  FiUserPlus, 
  FiUpload, 
  FiCheckCircle,
  FiClock,
  FiTrendingUp,
  FiDownload,
  FiAlertCircle,
  FiPhone,
  FiSettings,
  FiMail,
  FiXCircle,
  FiTrash2,
  FiZap,
  FiRefreshCw,
  FiMessageSquare,
  FiKey,
  FiDollarSign,
} from 'react-icons/fi';
import { Select } from 'chakra-react-select';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { InputControl, SubmitButton } from 'formik-chakra-ui';
import {
  collection,
  query,
  where,
  doc,
  updateDoc,
  onSnapshot,
  setDoc,
  getDoc,
  orderBy,
  deleteDoc,
} from 'firebase/firestore';
import { db } from '../../utils/init-firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { supabase, transformDealToCamelCase, transformContactToCamelCase } from '../../utils/supabase';
import { syncTagOwnershipOnReassignment } from '../../utils/tagOwnership';
import { useHistory, useLocation } from 'react-router-dom';
import { states } from '../../assets/allstates';
import { app } from '../../utils/init-firebase';
import TemplateManager from '../../components/CRM/Admin/TemplateManager';
import AuditLogViewer from '../../components/CRM/Admin/AuditLogViewer';
import CommunicationLog from '../../components/CRM/Admin/CommunicationLog';
import StageWorkflowEditor from '../../components/CRM/Admin/StageWorkflowEditor';
import ImportHistoryViewer from '../../components/CRM/Admin/ImportHistoryViewer';
import ImportModal from '../../components/CRM/Import/ImportModal';
import ContactTypesManager from '../../components/CRM/Settings/ContactTypesManager';
import TagsManager from '../../components/CRM/Settings/TagsManager';
import DealPartyRolesManager from '../../components/CRM/Settings/DealPartyRolesManager';
import CustomFieldsManager from '../../components/CRM/Settings/CustomFieldsManager';
import ActivityTypesManager from '../../components/CRM/Settings/ActivityTypesManager';
import PresidentsClubManager from '../../components/CRM/Settings/PresidentsClubManager';
import NetworkRelationshipsManager from '../../components/CRM/Settings/NetworkRelationshipsManager';
import DealStagesManager from '../../components/CRM/Settings/DealStagesManager';
import RoundRobinManager from '../../components/CRM/Admin/RoundRobinManager';
import WeekendCoverageManager from '../../components/CRM/Admin/WeekendCoverageManager';
import CampaignSendWindowManager from '../../components/CRM/Settings/CampaignSendWindowManager';
import HotLeadQueueManager from '../../components/CRM/Admin/HotLeadQueueManager';
import AriveSyncRulesAdmin from '../../components/CRM/Admin/AriveSyncRulesAdmin';
import { useTourActions } from '../../components/FeatureTour/FeatureTourProvider';
import ContactDetailDrawer from '../../components/CRM/ContactDetailDrawer';

import { useAuth } from '../../contexts/AuthContext';
import moment from 'moment';

const stateOptions = states.map(state => ({
  label: `${state.code} - ${state.name}`,
  value: state.code
}));

const userValidationSchema = Yup.object().shape({
  fullName: Yup.string().required('Full name is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  licensedStates: Yup.array(),
});

const TAB_SLUGS = [
  'users',            // 0 - CRM Users
  'unassigned',       // 1 - Unassigned
  'import',           // 2 - Import
  'auto-assignment',  // 3 - Auto Assignment
  'templates',        // 4 - Templates
  'audit-log',        // 5 - Audit Log
  'comms-log',        // 6 - Comms Log
  'stage-workflow',   // 7 - Stage Workflow
  'dialpad',          // 8 - Dialpad
  'crm-config',       // 9 - CRM Config
  'data-remediation', // 10 - Data Remediation
  'gmail-test',       // 11 - Gmail Test
  'hot-lead-queue',   // 12 - Hot Lead Queue
];
const SLUG_TO_INDEX = Object.fromEntries(TAB_SLUGS.map((s, i) => [s, i]));

function AdminPageContent() {
  const history = useHistory();
  const location = useLocation();
  const toast = useToast();
  const { currentUser } = useAuth();
  
  // State
  const [crmUsers, setCrmUsers] = useState([]);
  const [loanOfficers, setLoanOfficers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unassignedDeals, setUnassignedDeals] = useState([]);
  const [selectedDeals, setSelectedDeals] = useState([]);
  const [selectedUnassignedDeal, setSelectedUnassignedDeal] = useState(null);
  const [bulkAssignToId, setBulkAssignToId] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(location.search);
    const tabSlug = params.get('tab');
    return tabSlug && SLUG_TO_INDEX[tabSlug] !== undefined ? SLUG_TO_INDEX[tabSlug] : 0;
  });
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (activeTab > 0) {
      params.set('tab', TAB_SLUGS[activeTab]);
    } else {
      params.delete('tab');
    }
    const search = params.toString();
    const newSearch = search ? `?${search}` : '';
    if (location.search !== newSearch) {
      history.replace({ pathname: location.pathname, search: newSearch });
    }
  }, [activeTab, history, location.search, location.pathname]);
  const [unassignedSearchTerm, setUnassignedSearchTerm] = useState('');
  const [dealToDelete, setDealToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const deleteAlertCancelRef = useRef();

  // Migration state
  const [migrationRunning, setMigrationRunning] = useState(false);
  const [migrationReport, setMigrationReport] = useState(null);
  const [showMigrationReport, setShowMigrationReport] = useState(false);
  
  // Gmail signature test state
  const [testUserEmail, setTestUserEmail] = useState('');
  const [isTestingSignature, setIsTestingSignature] = useState(false);
  const [signatureResult, setSignatureResult] = useState(null);
  const [isSendingTestEmail, setIsSendingTestEmail] = useState(false);
  const [testEmailTo, setTestEmailTo] = useState('');
  
  // Contact drawer state (for clickable unassigned lead rows)
  const [selectedContactForDrawer, setSelectedContactForDrawer] = useState(null);

  // Disclosures
  const { isOpen: isUserModalOpen, onOpen: onUserModalOpen, onClose: onUserModalClose } = useDisclosure();
  const { isOpen: isDealDetailsOpen, onOpen: onDealDetailsOpen, onClose: onDealDetailsClose } = useDisclosure();
  const { isOpen: isDeleteAlertOpen, onOpen: onDeleteAlertOpen, onClose: onDeleteAlertClose } = useDisclosure();
  const { isOpen: isImportModalOpen, onOpen: onImportModalOpen, onClose: onImportModalClose } = useDisclosure();
  const { isOpen: isContactDrawerOpen, onOpen: onContactDrawerOpen, onClose: onContactDrawerClose } = useDisclosure();

  // FeatureTour: named actions the admin tours can invoke — click the tagged
  // tab header (never index math). Must stay above the loading early return.
  // (Sits below the disclosures because openImportForTour opens the import
  // modal for the Import Studio tour.)
  useTourActions(useMemo(() => {
    const clickTab = (id) => document.querySelector(`[data-tour="${id}"]`)?.click();
    return {
      showUsersTab: () => clickTab('admin-tab-users'),
      showUnassignedTab: () => clickTab('admin-tab-unassigned'),
      showImportTab: () => clickTab('admin-tab-import'),
      showAutoTab: () => clickTab('admin-tab-auto'),
      showTemplatesTab: () => clickTab('admin-tab-templates'),
      showStageWorkflowTab: () => clickTab('admin-tab-stagework'),
      showDialpadTab: () => clickTab('admin-tab-dialpad'),
      showConfigTab: () => clickTab('admin-tab-config'),
      showHotLeadTab: () => clickTab('admin-tab-hotlead'),
      showAriveRulesTab: () => clickTab('admin-tab-ariverules'),
      openImportForTour: () => {
        clickTab('admin-tab-import');
        onImportModalOpen();
      },
    };
  }, [onImportModalOpen]));

  // Theme colors
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const boxBG = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const headerBG = useColorModeValue('white', 'gray.800');
  const statBG = useColorModeValue('white', 'gray.800');
  const tableHeaderBG = useColorModeValue('gray.50', 'gray.800');
  const reportBG = useColorModeValue('gray.50', 'gray.700');
  const tableHoverBG = useColorModeValue('gray.50', 'gray.700');
  const blueHeaderBG = useColorModeValue('blue.50', 'blue.900');
  const blueAccentBG = useColorModeValue('blue.50', 'blue.900');

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'u') {
        e.preventDefault();
        setActiveTab(1); // Jump to Unassigned tab
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'i') {
        e.preventDefault();
        setActiveTab(2); // Jump to Import tab
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  // Fetch CRM users
  useEffect(() => {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('CRMUser', '==', true));

    const unsubscribe = onSnapshot(
      q,
      async (querySnapshot) => {
        const users = [];
        const userPromises = [];
        
        querySnapshot.forEach((docSnap) => {
          const userData = { uid: docSnap.id, ...docSnap.data() };
          
          if (!userData['Licensed States'] || userData['Licensed States'].length === 0) {
            userPromises.push(
              getDoc(doc(db, 'broker_licenses', docSnap.id)).then(licenseDoc => ({
                uid: docSnap.id,
                states: licenseDoc.exists() ? licenseDoc.data()?.states || [] : []
              }))
            );
          }
          users.push(userData);
        });

        if (userPromises.length > 0) {
          const licenseResults = await Promise.all(userPromises);
          licenseResults.forEach(({ uid, states }) => {
            const userIndex = users.findIndex(u => u.uid === uid);
            if (userIndex > -1 && !users[userIndex]['Licensed States']) {
              users[userIndex]['Licensed States'] = states;
            }
          });
        }

        const sortedUsers = sortCRMUsersWithDTCPriority(users, (u) => u.Name || u.name || '');
        setCrmUsers(sortedUsers);
        
        const los = sortedUsers.filter(
          (user) =>
            user.CRMRole === 'Loan Officer' ||
            user.Roles?.includes('Loan Officer') ||
            user.Roles?.includes('Mortgage Broker')
        );
        setLoanOfficers(los);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching CRM users:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  // Fetch unassigned deals from Supabase
  // "Unassigned" = owner_id is NULL, empty string, OR points to a user who
  // isn't a CRM user (i.e. not in crmUsers). Re-runs when crmUsers loads.
  useEffect(() => {
    // Wait for CRM users to load so we can build the NOT-IN list
    if (loading) return;

    const validCrmUids = crmUsers.map(u => u.uid).filter(Boolean);

    const fetchUnassignedDeals = async () => {
      try {
        let query = supabase
          .from('crm_deals')
          .select(`
            *,
            crm_contacts!contact_id (*)
          `)
          .order('created_at', { ascending: false });

        if (validCrmUids.length > 0) {
          // Deals where owner_id is NULL, empty, or not a known CRM user
          query = query.or(
            `owner_id.is.null,owner_id.eq.,owner_id.not.in.(${validCrmUids.join(',')})`
          );
        }
        // If no CRM users loaded yet, all deals are effectively unassigned

        const { data: dealsData, error: dealsError } = await query;

        if (dealsError) {
          console.error('Error fetching unassigned deals:', dealsError);
          return;
        }

        const enrichedDeals = (dealsData || []).map((deal) => {
          const dealCamel = transformDealToCamelCase(deal);
          const contact = deal.crm_contacts ? transformContactToCamelCase(deal.crm_contacts) : null;
          return {
            ...dealCamel,
            contact,
            dealName: dealCamel.dealName || 
              (contact ? `${contact.firstName || ''} ${contact.lastName || ''}`.trim() : 'Unnamed Deal'),
          };
        });

        setUnassignedDeals(enrichedDeals);
      } catch (error) {
        console.error('Error fetching unassigned deals:', error);
      }
    };

    fetchUnassignedDeals();

    // Subscribe to all deal changes — the refetch query handles the filtering
    const subscription = supabase
      .channel(`unassigned-deals-${Math.random().toString(36).slice(2, 8)}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'crm_deals',
      }, () => {
        fetchUnassignedDeals();
      })
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [crmUsers, loading]);

  const handleEditUser = (user) => {
    setSelectedUser(user);
    onUserModalOpen();
  };

  const handleBulkAssign = async () => {
    if (selectedDeals.length === 0 || !bulkAssignToId) {
      toast({
        title: 'Selection required',
        description: 'Please select deals and a Loan Officer',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setAssigning(true);

    try {
      const assignedUser = loanOfficers.find(lo => lo.uid === bulkAssignToId);
      const assignedDealIds = []; // Track successfully assigned deals for notifications

      // Update deals and contacts in Supabase
      for (const dealId of selectedDeals) {
        // Get deal to find its contact_id
        const { data: dealData, error: fetchError } = await supabase
          .from('crm_deals')
          .select('id, contact_id')
          .eq('id', dealId)
          .single();

        if (fetchError) {
          console.error(`Error fetching deal ${dealId}:`, fetchError);
          continue;
        }

        // Update deal
        const { error: dealError } = await supabase
          .from('crm_deals')
          .update({
            owner_id: bulkAssignToId,
            updated_at: new Date().toISOString(),
          })
          .eq('id', dealId);

        if (dealError) {
          console.error(`Error updating deal ${dealId}:`, dealError);
          continue;
        }

        assignedDealIds.push(dealId);

        // Update contact
        if (dealData?.contact_id) {
          const { error: contactError } = await supabase
            .from('crm_contacts')
            .update({
              owner_id: bulkAssignToId,
              updated_at: new Date().toISOString(),
            })
            .eq('id', dealData.contact_id);

          if (contactError) {
            console.error(`Error updating contact for deal ${dealId}:`, contactError);
          }
        }
      }

      const assignedUserName = assignedUser?.Name || 'user';
      const assignedUserEmail = assignedUser?.Email || assignedUser?.email || '';

      // Send email notifications if assigning to someone else
      if (currentUser?.uid !== bulkAssignToId && assignedUserEmail) {
        // Send notifications in the background (don't await)
        const functions = getFunctions(app, 'us-east1');
        const sendLeadAssignmentNotification = httpsCallable(functions, 'sendLeadAssignmentNotification');
        
        // Send notification for each assigned deal
        assignedDealIds.forEach(async (dealId) => {
          try {
            await sendLeadAssignmentNotification({
              dealId,
              assigneeId: bulkAssignToId,
              assigneeName: assignedUserName,
              assigneeEmail: assignedUserEmail,
            });
          } catch (notifError) {
            console.error(`Failed to send notification for deal ${dealId}:`, notifError);
            // Don't show error to user - assignment was successful
          }
        });
      }

      // Send ONE summary text to the new owner (not one per deal) — bulk assign of new leads.
      if (assignedDealIds.length > 0 && currentUser?.uid !== bulkAssignToId) {
        try {
          const notifyFns = getFunctions(app, 'us-east1');
          await httpsCallable(notifyFns, 'notifyLoanOfficerSMS')({
            assigneeId: bulkAssignToId,
            count: assignedDealIds.length,
            assignmentType: 'bulk_new_leads',
          });
        } catch (smsErr) {
          console.error('Bulk-assign summary SMS failed (non-blocking):', smsErr);
        }
      }

      // Sync tag ownership so the new owner can filter by existing tags
      // This ensures users can filter by tags on their reassigned deals
      if (assignedDealIds.length > 0) {
        await syncTagOwnershipOnReassignment('deal', assignedDealIds, bulkAssignToId);
      }

      toast({
        title: '✅ Assignment complete',
        description: `Assigned ${assignedDealIds.length} leads to ${assignedUserName}`,
        status: 'success',
        duration: 4000,
      });

      setSelectedDeals([]);
      setBulkAssignToId('');
    } catch (error) {
      console.error('Error assigning deals:', error);
      toast({
        title: 'Error assigning deals',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setAssigning(false);
    }
  };

  // Delete an unassigned lead (deal + contact) from Supabase
  const handleDeleteLead = async () => {
    if (!dealToDelete) return;
    
    setIsDeleting(true);
    
    try {
      // Delete the contact if it exists
      if (dealToDelete.contactId) {
        const { error: contactError } = await supabase
          .from('crm_contacts')
          .delete()
          .eq('id', dealToDelete.contactId);
        
        if (contactError) {
          console.error('Error deleting contact:', contactError);
        }
      }
      
      // Delete the deal
      const { error: dealError } = await supabase
        .from('crm_deals')
        .delete()
        .eq('id', dealToDelete.id);
      
      if (dealError) {
        throw dealError;
      }
      
      toast({
        title: '🗑️ Lead deleted',
        description: `${dealToDelete.dealName} has been removed`,
        status: 'success',
        duration: 3000,
      });
      
      // Remove from selected deals if it was selected
      setSelectedDeals(prev => prev.filter(id => id !== dealToDelete.id));
      
      onDeleteAlertClose();
      setDealToDelete(null);
    } catch (error) {
      console.error('Error deleting lead:', error);
      toast({
        title: 'Error deleting lead',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const toggleDealSelection = (dealId) => {
    setSelectedDeals((prev) =>
      prev.includes(dealId) ? prev.filter((id) => id !== dealId) : [...prev, dealId]
    );
  };

  const handleUnassignedRowClick = (deal) => {
    if (deal.contact) {
      setSelectedContactForDrawer(deal.contact);
      onContactDrawerOpen();
    }
  };

  const toggleSelectAll = () => {
    if (selectedDeals.length === unassignedDeals.length) {
      setSelectedDeals([]);
    } else {
      setSelectedDeals(unassignedDeals.map((deal) => deal.id));
    }
  };

  const handleTestSignature = async () => {
    setIsTestingSignature(true);
    setSignatureResult(null);
    
    try {
      const functions = getFunctions(app, 'us-east1');
      const fetchSignature = httpsCallable(functions, 'fetchUserSignature');
      
      // If testUserEmail is provided, we need to find that user's ID
      let targetUserId = null;
      if (testUserEmail && testUserEmail.trim()) {
        const targetUser = crmUsers.find(u => 
          (u.Email || u.email).toLowerCase() === testUserEmail.trim().toLowerCase()
        );
        if (targetUser) {
          targetUserId = targetUser.uid;
        } else {
          throw new Error(`User not found with email: ${testUserEmail}`);
        }
      }
      
      const result = await fetchSignature(targetUserId ? { userId: targetUserId } : {});
      
      if (result.data.success) {
        setSignatureResult(result.data);
        
        if (result.data.hasSignature) {
          toast({
            title: 'Signature found!',
            description: `Successfully retrieved Gmail signature (${result.data.signature.length} characters)`,
            status: 'success',
            duration: 4000,
          });
        } else {
          toast({
            title: 'No signature configured',
            description: `No Gmail signature found for ${testUserEmail || 'your account'}. Configure one in Gmail settings.`,
            status: 'info',
            duration: 4000,
          });
        }
      }
    } catch (error) {
      console.error('Error testing signature:', error);
      
      toast({
        title: 'Error fetching signature',
        description: error.message || 'Check console for details',
        status: 'error',
        duration: 5000,
      });
      
      setSignatureResult({ error: error.message });
    } finally {
      setIsTestingSignature(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmailTo || !testEmailTo.trim()) {
      toast({
        title: 'Recipient required',
        description: 'Please enter a recipient email address',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setIsSendingTestEmail(true);
    
    try {
      const functions = getFunctions(app, 'us-east1');
      const sendEmail = httpsCallable(functions, 'sendEmailWithSignature');
      
      // If testUserEmail is provided, find that user's ID
      let targetUserId = null;
      if (testUserEmail && testUserEmail.trim()) {
        const targetUser = crmUsers.find(u => 
          (u.Email || u.email).toLowerCase() === testUserEmail.trim().toLowerCase()
        );
        if (targetUser) {
          targetUserId = targetUser.uid;
        } else {
          throw new Error(`User not found with email: ${testUserEmail}`);
        }
      }
      
      const result = await sendEmail({
        to: testEmailTo.trim(),
        subject: 'Test Email with Gmail Signature',
        body: `<p>This is a test email sent from the CRM Admin panel.</p>
               <p>Testing Gmail signature integration with Service Account Domain-Wide Delegation.</p>
               <p><strong>Sent as:</strong> ${testUserEmail || 'Current User'}</p>
               <p><strong>Timestamp:</strong> ${new Date().toLocaleString()}</p>`,
        sendAsUserId: targetUserId, // If null, uses authenticated user
      });
      
      if (result.data.success) {
        toast({
          title: 'Test email sent!',
          description: `Email sent to ${testEmailTo} with signature`,
          status: 'success',
          duration: 5000,
        });
        setTestEmailTo('');
      }
    } catch (error) {
      console.error('Error sending test email:', error);
      
      toast({
        title: 'Error sending email',
        description: error.message || 'Check console for details',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsSendingTestEmail(false);
    }
  };

  const handleUserSubmit = async (values, actions) => {
    try {
      if (!selectedUser) return;

      const sortedStates = values.licensedStates ? [...values.licensedStates].sort() : [];

      const userRef = doc(db, 'users', selectedUser.uid);
      await updateDoc(userRef, {
        Name: values.fullName,
        Email: values.email,
        'Licensed States': sortedStates,
      });

      const licenseRef = doc(db, 'broker_licenses', selectedUser.uid);
      await setDoc(
        licenseRef,
        {
          states: sortedStates,
          updatedAt: new Date(),
          updatedBy: 'CRM Admin',
        },
        { merge: true }
      );

      toast({
        title: '✅ User updated',
        description: 'Changes synced across all systems',
        status: 'success',
        duration: 3000,
      });

      onUserModalClose();
      actions.resetForm();
      setSelectedUser(null);
    } catch (error) {
      console.error('Error saving user:', error);
      toast({
        title: 'Error saving user',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  // Helper function to format webhook data in a user-friendly way
  const formatWebhookData = (webhookData) => {
    if (!webhookData) return [];
    
    // Define friendly labels for common fields
    const fieldLabels = {
      firstName: 'First Name',
      first_name: 'First Name',
      lastName: 'Last Name',
      last_name: 'Last Name',
      email: 'Email',
      phone: 'Phone',
      phoneNumber: 'Phone',
      source: 'Lead Source',
      product: 'Loan Product',
      loanType: 'Loan Type',
      amount: 'Loan Amount',
      loanAmount: 'Loan Amount',
      leadCost: 'Lead Cost',
      cost: 'Lead Cost',
      campaignId: 'Campaign ID',
      campaign: 'Campaign',
      utm_campaign: 'Campaign',
      adGroupId: 'Ad Group ID',
      adGroup: 'Ad Group',
      utm_content: 'Ad Group',
      term: 'Keyword/Term',
      utm_term: 'Keyword/Term',
      medium: 'Medium',
      utm_medium: 'Medium',
      roundrobinName: 'Round Robin',
      roundRobinName: 'Round Robin',
      ownerId: 'Assigned To (ID)',
      assignTo: 'Assigned To',
      propertyAddress: 'Property Address',
      city: 'City',
      state: 'State',
      zip: 'ZIP Code',
      propertyType: 'Property Type',
      creditScore: 'Credit Score',
      downPayment: 'Down Payment',
      purchaseOrRefi: 'Purchase or Refi',
      loanPurpose: 'Loan Purpose',
      notes: 'Notes',
      leadId: 'External Lead ID',
      formName: 'Form Name',
      formId: 'Form ID',
      timeframe: 'Timeframe',
      referralSource: 'Referral Source',
      apiKey: '(API Key)',
    };

    // Fields to skip (internal/sensitive)
    const skipFields = ['apiKey', 'api_key', 'API_KEY'];

    const formatted = [];
    
    for (const [key, value] of Object.entries(webhookData)) {
      // Skip internal fields
      if (skipFields.includes(key)) continue;
      
      // Skip empty values
      if (value === null || value === undefined || value === '') continue;
      
      // Get friendly label
      const label = fieldLabels[key] || key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim();
      
      // Format the value
      let displayValue = value;
      if (typeof value === 'object') {
        displayValue = JSON.stringify(value, null, 2);
      } else if (key.toLowerCase().includes('amount') || key.toLowerCase().includes('cost')) {
        const num = parseFloat(value);
        if (!isNaN(num)) {
          displayValue = `$${num.toLocaleString()}`;
        }
      }
      
      formatted.push({ label, value: displayValue, key });
    }
    
    return formatted;
  };

  if (loading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Center h="60vh" flexDir="column">
          <Spinner size="xl" color="blue.500" thickness="4px" mb={4} />
          <Text color="gray.600">Loading CRM Admin...</Text>
        </Center>
      </Container>
    );
  }

  const totalStates = crmUsers.reduce((sum, user) => sum + (user['Licensed States']?.length || 0), 0);
  const avgStates = crmUsers.length > 0 ? Math.round(totalStates / crmUsers.length) : 0;

  return (
    <Box bg={bgColor} minH="100vh">
      <Container maxW="100%" px={{ base: 4, md: 6, lg: 8 }} py={6}>
        <VStack align="stretch" spacing={6} maxW="1600px" mx="auto">
          {/* Header */}
          <Box bg={headerBG} p={6} borderRadius="xl" shadow="sm">
            <HStack justify="space-between" align="flex-start">
              <HStack spacing={3}>
                <Tooltip label="Back to My Deals">
                  <IconButton
                    icon={<FiArrowLeft />}
                    aria-label="Back to My Deals"
                    onClick={() => history.push('/crm/my-deals')}
                    size="lg"
                    variant="ghost"
                  />
                </Tooltip>
                <VStack align="flex-start" spacing={1} data-tour="admin-header">
                  <Heading size="lg">⚙️ CRM Admin</Heading>
                  <Text color="gray.600" fontSize="sm">
                    Manage users, assign leads, and import data
                  </Text>
                </VStack>
              </HStack>
              <HStack spacing={3}>
                <Button
                  leftIcon={<FiDollarSign />}
                  colorScheme="green"
                  variant="outline"
                  onClick={() => history.push('/crm/usage')}
                >
                  Usage &amp; Billing
                </Button>
                <Button
                  leftIcon={<FiKey />}
                  colorScheme="blue"
                  variant="outline"
                  onClick={() => history.push('/crm/admin/api-keys')}
                >
                  API Keys
                </Button>
              </HStack>
            </HStack>

            {/* Quick Stats */}
            <Grid templateColumns="repeat(4, 1fr)" gap={4} mt={6} data-tour="admin-stats">
              <GridItem>
                <Stat>
                  <StatLabel fontSize="xs">Total CRM Users</StatLabel>
                  <StatNumber>{crmUsers.length}</StatNumber>
                  <StatHelpText>{loanOfficers.length} Loan Officers</StatHelpText>
                </Stat>
              </GridItem>
              <GridItem>
                <Stat>
                  <StatLabel fontSize="xs">Unassigned Leads</StatLabel>
                  <StatNumber color={unassignedDeals.length > 0 ? 'orange.500' : 'green.500'}>
                    {unassignedDeals.length}
                  </StatNumber>
                  <StatHelpText>
                    {unassignedDeals.length > 0 ? 'need assignment' : 'all assigned'}
                  </StatHelpText>
                </Stat>
              </GridItem>
              <GridItem>
                <Stat>
                  <StatLabel fontSize="xs">Avg. Licensed States</StatLabel>
                  <StatNumber>{avgStates}</StatNumber>
                  <StatHelpText>{totalStates} total</StatHelpText>
                </Stat>
              </GridItem>
              <GridItem>
                <Stat>
                  <StatLabel fontSize="xs">Last Import</StatLabel>
                  <StatNumber fontSize="md">—</StatNumber>
                  <StatHelpText>No recent imports</StatHelpText>
                </Stat>
              </GridItem>
            </Grid>
          </Box>

          {/* Tabs */}
          <Tabs index={activeTab} onChange={setActiveTab} colorScheme="blue">
            <TabList bg={boxBG} borderRadius="lg" p={1} shadow="sm" data-tour="admin-tabs">
              <Tab borderRadius="md" data-tour="admin-tab-users">
                <HStack spacing={2}>
                  <Icon as={FiUsers} />
                  <Text>CRM Users</Text>
                  <Badge borderRadius="full">{crmUsers.length}</Badge>
                </HStack>
              </Tab>
              <Tab borderRadius="md" data-tour="admin-tab-unassigned">
                <HStack spacing={2}>
                  <Icon as={FiClock} />
                  <Text>Unassigned</Text>
                  {unassignedDeals.length > 0 && (
                    <Badge colorScheme="orange" borderRadius="full">
                      {unassignedDeals.length}
                    </Badge>
                  )}
                  <Kbd fontSize="xs">⌘U</Kbd>
                </HStack>
              </Tab>
              <Tab borderRadius="md" data-tour="admin-tab-import">
                <HStack spacing={2}>
                  <Icon as={FiUpload} />
                  <Text>Import</Text>
                  <Kbd fontSize="xs">⌘I</Kbd>
                </HStack>
              </Tab>
              <Tab borderRadius="md" data-tour="admin-tab-auto">
                <HStack spacing={2}>
                  <Icon as={FiUsers} />
                  <Text>Auto Assignment</Text>
                </HStack>
              </Tab>
              <Tab borderRadius="md" data-tour="admin-tab-templates">
                <HStack spacing={2}>
                  <Icon as={FiAlertCircle} />
                  <Text>Templates</Text>
                </HStack>
              </Tab>
              <Tab borderRadius="md">
                <HStack spacing={2}>
                  <Icon as={FiCheckCircle} />
                  <Text>Audit Log</Text>
                </HStack>
              </Tab>
              <Tab borderRadius="md">
                <HStack spacing={2}>
                  <Icon as={FiMessageSquare} />
                  <Text>Comms Log</Text>
                </HStack>
              </Tab>
              <Tab borderRadius="md" data-tour="admin-tab-stagework">
                <HStack spacing={2}>
                  <Icon as={FiTrendingUp} />
                  <Text>Stage Workflow</Text>
                </HStack>
              </Tab>
              <Tab borderRadius="md" data-tour="admin-tab-dialpad">
                <HStack spacing={2}>
                  <Icon as={FiPhone} />
                  <Text>Dialpad</Text>
                </HStack>
              </Tab>
              <Tab borderRadius="md" data-tour="admin-tab-config">
                <HStack spacing={2}>
                  <Icon as={FiSettings} />
                  <Text>CRM Config</Text>
                </HStack>
              </Tab>
              <Tab borderRadius="md">
                <HStack spacing={2}>
                  <Icon as={FiAlertCircle} />
                  <Text>Data Remediation</Text>
                </HStack>
              </Tab>
              <Tab borderRadius="md">
                <HStack spacing={2}>
                  <Icon as={FiMail} />
                  <Text>Gmail Test</Text>
                </HStack>
              </Tab>
              <Tab borderRadius="md" data-tour="admin-tab-hotlead">
                <HStack spacing={2}>
                  <Icon as={FiZap} />
                  <Text>Hot Lead Queue</Text>
                </HStack>
              </Tab>
              <Tab borderRadius="md" data-tour="admin-tab-ariverules">
                <HStack spacing={2}>
                  <Icon as={FiZap} />
                  <Text>Arive Sync Rules</Text>
                </HStack>
              </Tab>
            </TabList>

            <TabPanels>
              {/* CRM USERS TAB */}
              <TabPanel px={0}>
                <Fade in>
                  <Box bg={boxBG} borderRadius="xl" p={6} shadow="sm" data-tour="admin-users-table">
                    <HStack justify="space-between" mb={4}>
                      <VStack align="flex-start" spacing={0}>
                        <Heading size="md">CRM Users</Heading>
                        <Text fontSize="sm" color="gray.600">
                          Manage user details and licenses
                        </Text>
                      </VStack>
                    </HStack>

                    <Box overflowX="auto">
                      <Table variant="simple">
                        <Thead bg={tableHeaderBG}>
                          <Tr>
                            <Th>Name</Th>
                            <Th>Email</Th>
                            <Th>CRM Role</Th>
                            <Th>Branch</Th>
                            <Th>NMLS</Th>
                            <Th>Licensed States</Th>
                            <Th>Actions</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {crmUsers.map((user) => (
                            <Tr key={user.uid} _hover={{ bg: tableHoverBG }}>
                              <Td fontWeight="medium">{user.Name || user.name}</Td>
                              <Td fontSize="sm">{user.Email || user.email}</Td>
                              <Td>
                                <Badge colorScheme="blue">
                                  {user.CRMRole || 'Not Set'}
                                </Badge>
                              </Td>
                              <Td>{user.Branch || '—'}</Td>
                              <Td>{user.NMLS || '—'}</Td>
                              <Td>
                                {user['Licensed States'] && user['Licensed States'].length > 0 ? (
                                  <Wrap spacing={1}>
                                    {user['Licensed States'].slice(0, 3).map((code) => (
                                      <Badge key={code} size="sm" colorScheme="green">
                                        {code}
                                      </Badge>
                                    ))}
                                    {user['Licensed States'].length > 3 && (
                                      <Text fontSize="xs" color="gray.500">
                                        +{user['Licensed States'].length - 3}
                                      </Text>
                                    )}
                                  </Wrap>
                                ) : (
                                  <Text fontSize="sm" color="gray.400">—</Text>
                                )}
                              </Td>
                              <Td>
                                <Button
                                  size="sm"
                                  colorScheme="blue"
                                  variant="outline"
                                  onClick={() => handleEditUser(user)}
                                >
                                  Edit
                                </Button>
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>

                    {crmUsers.length === 0 && (
                      <Center py={12}>
                        <VStack>
                          <Icon as={FiUsers} boxSize={12} color="gray.300" />
                          <Text color="gray.500" fontSize="sm">
                            No CRM users found
                          </Text>
                          <Text fontSize="xs" color="gray.400">
                            Enable CRM access on the main Admin page
                          </Text>
                        </VStack>
                      </Center>
                    )}
                  </Box>
                </Fade>
              </TabPanel>

              {/* UNASSIGNED LEADS TAB */}
              <TabPanel px={0}>
                <Fade in>
                  <Box bg={boxBG} borderRadius="xl" p={6} shadow="sm" data-tour="admin-unassigned">
                    <VStack align="stretch" spacing={4}>
                      <HStack justify="space-between" flexWrap="wrap" gap={3}>
                        <VStack align="flex-start" spacing={0}>
                          <Heading size="md">Unassigned Leads</Heading>
                          <Text fontSize="sm" color="gray.600">
                            Distribute leads to your team
                          </Text>
                        </VStack>
                        <HStack spacing={3}>
                          <Input
                            placeholder="Search by name..."
                            value={unassignedSearchTerm}
                            onChange={(e) => setUnassignedSearchTerm(e.target.value)}
                            maxW="250px"
                            size="sm"
                          />
                          {selectedDeals.length > 0 && (
                            <ScaleFade in>
                              <Badge colorScheme="blue" fontSize="md" px={3} py={1}>
                                {selectedDeals.length} selected
                              </Badge>
                            </ScaleFade>
                          )}
                        </HStack>
                      </HStack>

                      {(() => {
                        const filteredUnassignedDeals = unassignedDeals.filter(deal => 
                          !unassignedSearchTerm || 
                          deal.dealName?.toLowerCase().includes(unassignedSearchTerm.toLowerCase())
                        );
                        
                        return filteredUnassignedDeals.length > 0 ? (
                        <>
                          <Box overflowX="auto">
                            <Table variant="simple" size="sm">
                              <Thead bg={blueHeaderBG}>
                                <Tr>
                                  <Th>
                                    <Checkbox
                                      isChecked={selectedDeals.length === filteredUnassignedDeals.length && filteredUnassignedDeals.length > 0}
                                      onChange={() => {
                                        if (selectedDeals.length === filteredUnassignedDeals.length) {
                                          setSelectedDeals([]);
                                        } else {
                                          setSelectedDeals(filteredUnassignedDeals.map((deal) => deal.id));
                                        }
                                      }}
                                    />
                                  </Th>
                                  <Th>Name</Th>
                                  <Th>Contact</Th>
                                  <Th>Source</Th>
                                  <Th>Product</Th>
                                  <Th>State</Th>
                                  <Th>Campaign</Th>
                                  <Th>Amount</Th>
                                  <Th>Created</Th>
                                  <Th>Actions</Th>
                                </Tr>
                              </Thead>
                              <Tbody>
                                {filteredUnassignedDeals.map((deal) => (
                                  <Tr key={deal.id} _hover={{ bg: tableHoverBG, cursor: 'pointer' }} onClick={() => handleUnassignedRowClick(deal)}>
                                    <Td onClick={(e) => e.stopPropagation()}>
                                      <Checkbox
                                        isChecked={selectedDeals.includes(deal.id)}
                                        onChange={() => toggleDealSelection(deal.id)}
                                      />
                                    </Td>
                                    <Td fontWeight="medium">{deal.dealName}</Td>
                                    <Td fontSize="sm">
                                      <VStack align="flex-start" spacing={0}>
                                        <Text>{deal.contact?.phone || '—'}</Text>
                                        <Text fontSize="xs" color="gray.500">{deal.contact?.email || '—'}</Text>
                                      </VStack>
                                    </Td>
                                    <Td>
                                      <Badge size="sm">{deal.source}</Badge>
                                    </Td>
                                    <Td fontSize="sm">{deal.product || '—'}</Td>
                                    <Td fontSize="sm">{deal.propertyAddress?.state || '—'}</Td>
                                    <Td fontSize="sm">
                                      {deal.sourceDetails?.campaignId || '—'}
                                    </Td>
                                    <Td fontSize="sm" fontWeight="semibold">
                                      {deal.amount > 0 ? `$${deal.amount.toLocaleString()}` : '—'}
                                    </Td>
                                    <Td fontSize="xs" color="gray.500">
                                      {deal.createdAt
                                        ? moment(deal.createdAt).fromNow()
                                        : '—'}
                                    </Td>
                                    <Td onClick={(e) => e.stopPropagation()}>
                                      <HStack spacing={1}>
                                        {deal.sourceDetails?.webhookData && (
                                          <Button 
                                            size="xs" 
                                            onClick={() => {
                                              setSelectedUnassignedDeal(deal);
                                              onDealDetailsOpen();
                                            }}
                                          >
                                            View Details
                                          </Button>
                                        )}
                                        <IconButton
                                          icon={<FiTrash2 />}
                                          size="xs"
                                          colorScheme="red"
                                          variant="ghost"
                                          aria-label="Delete lead"
                                          onClick={() => {
                                            setDealToDelete(deal);
                                            onDeleteAlertOpen();
                                          }}
                                        />
                                      </HStack>
                                    </Td>
                                  </Tr>
                                ))}
                              </Tbody>
                            </Table>
                          </Box>

                          {/* Assignment Section */}
                          <Box p={4} bg={blueAccentBG} borderRadius="lg">
                            <HStack spacing={4} flexWrap="wrap">
                              <Text fontWeight="semibold" fontSize="sm">
                                Assign {selectedDeals.length || 'selected'} lead{selectedDeals.length !== 1 ? 's' : ''} to:
                              </Text>
                              <ChakraSelect
                                placeholder="Select Loan Officer"
                                value={bulkAssignToId}
                                onChange={(e) => setBulkAssignToId(e.target.value)}
                                size="sm"
                                maxW="250px"
                              >
                                {(() => {
                                  const { dtcUsers, otherUsers } = groupCRMUsersByDTC(loanOfficers, (u) => u.Name || u.name || '');
                                  return (
                                    <>
                                      {dtcUsers.length > 0 && (
                                        <optgroup label="DTC Loan Officers">
                                          {dtcUsers.map((lo) => (
                                            <option key={lo.uid} value={lo.uid}>
                                              {lo.Name || lo.name}
                                            </option>
                                          ))}
                                        </optgroup>
                                      )}
                                      <optgroup label={dtcUsers.length > 0 ? 'Other Loan Officers' : 'Loan Officers'}>
                                        {otherUsers.map((lo) => (
                                          <option key={lo.uid} value={lo.uid}>
                                            {lo.Name || lo.name}
                                          </option>
                                        ))}
                                      </optgroup>
                                    </>
                                  );
                                })()}
                              </ChakraSelect>
                              <Button
                                colorScheme="blue"
                                size="sm"
                                onClick={handleBulkAssign}
                                isLoading={assigning}
                                isDisabled={selectedDeals.length === 0 || !bulkAssignToId}
                                leftIcon={<FiCheckCircle />}
                              >
                                Assign Selected
                              </Button>
                            </HStack>
                          </Box>
                        </>
                      ) : (
                        <Center py={16}>
                          <VStack>
                            <Icon as={FiCheckCircle} boxSize={16} color="green.300" />
                            <Text color="gray.600" fontSize="lg" fontWeight="medium">
                              {unassignedSearchTerm ? 'No matching leads found' : 'All leads assigned!'}
                            </Text>
                            <Text fontSize="sm" color="gray.500">
                              {unassignedSearchTerm 
                                ? `No leads match "${unassignedSearchTerm}"` 
                                : 'No unassigned leads at the moment'}
                            </Text>
                          </VStack>
                        </Center>
                      );
                      })()}
                    </VStack>
                  </Box>
                </Fade>
              </TabPanel>

              {/* IMPORT TAB */}
              <TabPanel px={0}>
                <Fade in>
                  <Box bg={boxBG} borderRadius="xl" p={6} shadow="sm">
                    <VStack align="stretch" spacing={6}>
                      <VStack align="flex-start" spacing={0}>
                        <Heading size="md">Bulk Lead Import</Heading>
                        <Text fontSize="sm" color="gray.600">
                          Import contacts or RETR loan officers from CSV with column mapping, validation, and assignment
                        </Text>
                      </VStack>

                      <Button
                        colorScheme="blue"
                        size="lg"
                        onClick={onImportModalOpen}
                        leftIcon={<FiUpload />}
                        width="full"
                        data-tour="admin-import-btn"
                      >
                        Import CSV
                      </Button>

                      <ImportModal
                        isOpen={isImportModalOpen}
                        onClose={onImportModalClose}
                        importType="contacts-with-assignment"
                        allowAssignmentSelection={true}
                        crmUsers={crmUsers}
                        onImportComplete={() => {
                          toast({
                            title: 'Import complete',
                            description: 'Contacts have been imported successfully.',
                            status: 'success',
                            duration: 3000,
                          });
                        }}
                      />

                      {/* Import History */}
                      <Divider />
                      <ImportHistoryViewer />
                    </VStack>
                  </Box>
                </Fade>
              </TabPanel>

              {/* AUTOMATIC ASSIGNMENT TAB */}
              <TabPanel px={0}>
                <Fade in>
                  <VStack spacing={6} align="stretch" data-tour="admin-auto-assign">
                    <CampaignSendWindowManager />
                    <WeekendCoverageManager crmUsers={crmUsers} />
                    <RoundRobinManager crmUsers={crmUsers} />
                  </VStack>
                </Fade>
              </TabPanel>

              {/* TEMPLATES TAB */}
              <TabPanel px={0}>
                <Fade in>
                  <Box bg={boxBG} borderRadius="xl" p={6} shadow="sm" data-tour="admin-templates">
                    <TemplateManager />
                  </Box>
                </Fade>
              </TabPanel>

              {/* AUDIT LOG TAB */}
              <TabPanel px={0}>
                <Fade in>
                  <Box bg={boxBG} borderRadius="xl" p={6} shadow="sm">
                    <AuditLogViewer />
                  </Box>
                </Fade>
              </TabPanel>

              {/* COMMS LOG TAB */}
              <TabPanel px={0}>
                <Fade in>
                  <Box bg={boxBG} borderRadius="xl" p={6} shadow="sm">
                    <CommunicationLog />
                  </Box>
                </Fade>
              </TabPanel>

              {/* STAGE WORKFLOW TAB */}
              <TabPanel px={0}>
                <Fade in>
                  <Box bg={boxBG} borderRadius="xl" p={6} shadow="sm" data-tour="admin-stage-workflow">
                    <StageWorkflowEditor />
                  </Box>
                </Fade>
              </TabPanel>

              {/* DIALPAD SETTINGS TAB */}
              <TabPanel px={0}>
                <Fade in>
                  <Box bg={boxBG} borderRadius="xl" p={6} shadow="sm" data-tour="admin-dialpad">
                    <VStack align="stretch" spacing={6}>
                      <VStack align="flex-start" spacing={0}>
                        <Heading size="md">Dialpad Integration</Heading>
                        <Text fontSize="sm" color="gray.600">
                          Control which users have access to Dialpad features
                        </Text>
                      </VStack>

                      <Alert status="info" borderRadius="md">
                        <AlertIcon />
                        <VStack align="flex-start" spacing={1} flex={1}>
                          <Text fontSize="sm" fontWeight="semibold">
                            How This Works
                          </Text>
                          <Text fontSize="xs">
                            <strong>Step 1:</strong> Enable "Dialpad Capable" for users who should have access to Dialpad features.
                            <br />
                            <strong>Step 2:</strong> Users can then set up their Dialpad integration in CRM Settings.
                            <br />
                            If a user is not "Dialpad Capable", they won't see any Dialpad options anywhere in the CRM.
                          </Text>
                        </VStack>
                      </Alert>

                      <Divider />

                      <Box overflowX="auto">
                        <Table variant="simple" size="sm">
                          <Thead bg={tableHeaderBG}>
                            <Tr>
                              <Th>Name</Th>
                              <Th>Email</Th>
                              <Th>Dialpad Capable</Th>
                              <Th>Integration Status</Th>
                              <Th>Dialpad User ID</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {crmUsers.map((user) => (
                              <Tr key={user.uid} _hover={{ bg: tableHoverBG }}>
                                <Td fontWeight="medium">{user.Name || user.name}</Td>
                                <Td fontSize="sm">{user.Email || user.email}</Td>
                                <Td>
                                  <HStack spacing={2}>
                                    <Switch
                                      colorScheme="blue"
                                      isChecked={user.dialpadCapable === true}
                                      onChange={async (e) => {
                                        try {
                                          const userRef = doc(db, 'users', user.uid);
                                          await updateDoc(userRef, {
                                            dialpadCapable: e.target.checked,
                                          });
                                          
                                          // If disabling capability, also disable integration
                                          if (!e.target.checked && user.dialpadEnabled) {
                                            await updateDoc(userRef, {
                                              dialpadEnabled: false,
                                              dialpadUserId: null,
                                              dialpadUserInfo: null,
                                            });
                                          }
                                          
                                          toast({
                                            title: e.target.checked ? 'Dialpad access granted' : 'Dialpad access removed',
                                            description: e.target.checked 
                                              ? `${user.Name || user.name} can now set up Dialpad in Settings`
                                              : `${user.Name || user.name} no longer has access to Dialpad`,
                                            status: 'success',
                                            duration: 3000,
                                          });
                                        } catch (error) {
                                          console.error('Error updating dialpadCapable:', error);
                                          toast({
                                            title: 'Error updating access',
                                            description: error.message,
                                            status: 'error',
                                            duration: 5000,
                                          });
                                        }
                                      }}
                                    />
                                    <Text fontSize="xs" fontWeight="semibold" color={user.dialpadCapable ? 'green.600' : 'gray.500'}>
                                      {user.dialpadCapable ? 'Yes' : 'No'}
                                    </Text>
                                  </HStack>
                                </Td>
                                <Td>
                                  {!user.dialpadCapable ? (
                                    <Badge colorScheme="gray" fontSize="xs">
                                      Not Allowed
                                    </Badge>
                                  ) : user.dialpadEnabled ? (
                                    <Badge colorScheme="green" fontSize="xs">
                                      <HStack spacing={1}>
                                        <Icon as={FiCheckCircle} boxSize={2} />
                                        <Text>Connected</Text>
                                      </HStack>
                                    </Badge>
                                  ) : (
                                    <Badge colorScheme="orange" fontSize="xs">
                                      Not Set Up
                                    </Badge>
                                  )}
                                </Td>
                                <Td fontSize="xs" fontFamily="mono" color="gray.600">
                                  {user.dialpadUserId || '—'}
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </Box>

                      <Divider />

                      <Box p={4} bg="orange.50" borderRadius="md" borderWidth="1px" borderColor="orange.200">
                        <VStack align="stretch" spacing={3}>
                          <HStack spacing={2}>
                            <Icon as={FiAlertCircle} color="orange.600" />
                            <Text fontSize="sm" fontWeight="semibold">
                              One-Time Setup Required
                            </Text>
                          </HStack>
                          <Text fontSize="xs" color="gray.700">
                            Before using Dialpad, you need to set up webhooks. This only needs to be done once.
                            Click the button below to configure Dialpad integration.
                          </Text>
                          <Button
                            colorScheme="orange"
                            size="sm"
                            leftIcon={<FiCheckCircle />}
                            onClick={async () => {
                              try {
                                const functions = getFunctions(app, 'us-east1');
                                const setup = httpsCallable(functions, 'setupDialpadWebhooks');
                                toast({
                                  title: 'Setting up Dialpad...',
                                  description: 'This may take a moment',
                                  status: 'info',
                                  duration: 2000,
                                });
                                const result = await setup({});
                                if (result.data.success) {
                                  toast({
                                    title: 'Dialpad webhooks configured!',
                                    description: `Webhook ID: ${result.data.webhookId}`,
                                    status: 'success',
                                    duration: 5000,
                                  });
                                }
                              } catch (error) {
                                console.error('Error setting up webhooks:', error);
                                toast({
                                  title: 'Setup failed',
                                  description: error.message,
                                  status: 'error',
                                  duration: 5000,
                                });
                              }
                            }}
                          >
                            Set Up Dialpad Webhooks (One-Time)
                          </Button>
                          <Text fontSize="2xs" color="gray.500">
                            ℹ️ This creates webhook subscriptions in Dialpad for call and SMS events.
                            You'll see a success message when complete.
                          </Text>
                        </VStack>
                      </Box>
                    </VStack>
                  </Box>
                </Fade>
              </TabPanel>

              {/* CRM CONFIGURATION TAB - Origin CRM Settings */}
              <TabPanel px={0}>
                <Fade in>
                  <Box bg={boxBG} borderRadius="xl" p={6} shadow="sm">
                    <VStack align="stretch" spacing={8}>
                      <VStack align="flex-start" spacing={0}>
                        <Heading size="lg">Origin CRM Configuration</Heading>
                        <Text fontSize="sm" color="gray.600">
                          Customize contact types, deal party roles, and custom fields
                        </Text>
                      </VStack>

                      {/* Contact Types */}
                      <Box>
                        <ContactTypesManager />
                      </Box>

                      <Divider />

                      {/* Tags (Contact & Deal) - Managed in Supabase */}
                      <Box data-tour="admin-config-tags">
                        <TagsManager />
                      </Box>

                      <Divider />

                      {/* Deal Party Roles */}
                      <Box>
                        <DealPartyRolesManager />
                      </Box>

                      <Divider />

                      {/* Custom Fields */}
                      <Box>
                        <CustomFieldsManager />
                      </Box>

                      <Divider />

                      {/* Network Relationship Types */}
                      <Box>
                        <NetworkRelationshipsManager />
                      </Box>

                      <Divider />

                      {/* Activity Types (Dispositions & Next Actions) */}
                      <Box>
                        <ActivityTypesManager />
                      </Box>

                      <Divider />

                      {/* Deal Stages */}
                      <Box data-tour="admin-config-stages">
                        <DealStagesManager />
                      </Box>

                      <Divider />

                      {/* President's Club targets */}
                      <Box data-tour="admin-config-pc">
                        <PresidentsClubManager />
                      </Box>

                    </VStack>
                  </Box>
                </Fade>
              </TabPanel>

              {/* DATA REMEDIATION TAB */}
              <TabPanel px={0}>
                <Fade in>
                  <Box bg={boxBG} borderRadius="xl" p={6} shadow="sm">
                    <VStack align="stretch" spacing={6}>
                      <VStack align="flex-start" spacing={0}>
                        <Heading size="lg">Data Remediation</Heading>
                        <Text fontSize="sm" color="gray.600">
                          Fix historical data integrity issues from schema evolution
                        </Text>
                      </VStack>

                      <Alert status="info" borderRadius="md">
                        <AlertIcon />
                        <AlertDescription fontSize="sm">
                          This tool fixes two critical issues: (1) migrates legacy <Kbd>estCloseDate</Kbd> field to <Kbd>estimatedClosingDate</Kbd> in deals, and (2) normalizes email/phone formatting in contacts for consistent duplicate detection.
                        </AlertDescription>
                      </Alert>

                      {/* Remediation Controls */}
                      <Box borderWidth="1px" borderRadius="lg" p={6}>
                        <VStack align="stretch" spacing={4}>
                          <HStack spacing={4}>
                            <Button
                              colorScheme="blue"
                              variant="outline"
                              size="lg"
                              leftIcon={<Icon as={FiCheckCircle} />}
                              onClick={async () => {
                                try {
                                  toast({
                                    title: 'Running Dry Run...',
                                    description: 'Scanning database for issues (no changes will be made)',
                                    status: 'info',
                                    duration: 3000,
                                    isClosable: true,
                                  });

                                  const functions = getFunctions(app, 'us-central1');
                                  const remediateData = httpsCallable(functions, 'crmRemediateV1Data');
                                  const result = await remediateData({ dryRun: true });

                                  if (result && result.data) {
                                    setMigrationReport(result.data);
                                    setShowMigrationReport(true);

                                    toast({
                                      title: 'Dry Run Complete',
                                      description: `Found ${result.data.totalUpdates || 0} records that need updating`,
                                      status: 'success',
                                      duration: 5000,
                                      isClosable: true,
                                    });
                                  }
                                } catch (error) {
                                  console.error('Dry run failed:', error);
                                  toast({
                                    title: 'Dry Run Failed',
                                    description: error.message,
                                    status: 'error',
                                    duration: 5000,
                                    isClosable: true,
                                  });
                                }
                              }}
                              isDisabled={migrationRunning}
                            >
                              Run Dry Run
                            </Button>

                            <Button
                              colorScheme="red"
                              size="lg"
                              leftIcon={<Icon as={FiAlertCircle} />}
                              onClick={async () => {
                                const confirmed = window.confirm(
                                  'Are you sure you want to run LIVE remediation? This will modify production data.\n\n' +
                                  'Recommended: Run a dry run first to preview changes.'
                                );

                                if (!confirmed) return;

                                try {
                                  setMigrationRunning(true);
                                  toast({
                                    title: 'Running Remediation...',
                                    description: 'Updating database records (this may take a few minutes)',
                                    status: 'info',
                                    duration: 3000,
                                    isClosable: true,
                                  });

                                  const functions = getFunctions(app, 'us-central1');
                                  const remediateData = httpsCallable(functions, 'crmRemediateV1Data');
                                  const result = await remediateData({ dryRun: false });

                                  if (result && result.data) {
                                    setMigrationReport(result.data);
                                    setShowMigrationReport(true);

                                    toast({
                                      title: 'Remediation Complete',
                                      description: `Successfully updated ${result.data.totalUpdates || 0} records`,
                                      status: 'success',
                                      duration: 8000,
                                      isClosable: true,
                                    });
                                  }
                                } catch (error) {
                                  console.error('Remediation failed:', error);
                                  toast({
                                    title: 'Remediation Failed',
                                    description: error.message,
                                    status: 'error',
                                    duration: 8000,
                                    isClosable: true,
                                  });
                                } finally {
                                  setMigrationRunning(false);
                                }
                              }}
                              isDisabled={migrationRunning}
                              isLoading={migrationRunning}
                            >
                              Execute Remediation (LIVE)
                            </Button>
                          </HStack>

                          <Text fontSize="xs" color="gray.500">
                            ⚠️ <strong>Dry Run:</strong> Scans database and reports what would be changed (no actual changes made)<br />
                            🔴 <strong>Live Execution:</strong> Permanently modifies production data (run dry run first!)
                          </Text>
                        </VStack>
                      </Box>

                      {/* Remediation Report */}
                      {showMigrationReport && migrationReport && (
                        <Box borderWidth="1px" borderRadius="lg" p={6} bg={reportBG}>
                          <VStack align="stretch" spacing={4}>
                            <HStack justify="space-between">
                              <Heading size="md">
                                {migrationReport?.dryRun ? 'Dry Run Report' : 'Remediation Report'}
                              </Heading>
                              <Badge colorScheme={migrationReport?.success ? 'green' : 'red'} fontSize="md" px={3} py={1}>
                                {migrationReport?.success ? 'SUCCESS' : 'FAILED'}
                              </Badge>
                            </HStack>

                            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                              <Stat>
                                <StatLabel>Deals Checked</StatLabel>
                                <StatNumber>{migrationReport?.dealsChecked || 0}</StatNumber>
                                <StatHelpText>Total deals scanned</StatHelpText>
                              </Stat>
                              <Stat>
                                <StatLabel>Deals Updated</StatLabel>
                                <StatNumber color="blue.500">{migrationReport?.dealsUpdated || 0}</StatNumber>
                                <StatHelpText>
                                  {migrationReport?.dryRun ? 'Would be updated' : 'Actually updated'}
                                </StatHelpText>
                              </Stat>
                              <Stat>
                                <StatLabel>Contacts Checked</StatLabel>
                                <StatNumber>{migrationReport?.contactsChecked || 0}</StatNumber>
                                <StatHelpText>Total contacts scanned</StatHelpText>
                              </Stat>
                              <Stat>
                                <StatLabel>Contacts Updated</StatLabel>
                                <StatNumber color="blue.500">{migrationReport?.contactsUpdated || 0}</StatNumber>
                                <StatHelpText>
                                  {migrationReport?.dryRun ? 'Would be updated' : 'Actually updated'}
                                </StatHelpText>
                              </Stat>
                            </Grid>

                            <Box>
                              <Text fontWeight="bold" mb={2}>Summary:</Text>
                              <Text fontSize="sm">{migrationReport?.message || 'No message available'}</Text>
                            </Box>

                            {migrationReport?.errors && migrationReport?.errors?.length > 0 && (
                              <Box>
                                <Text fontWeight="bold" mb={2} color="red.500">
                                  Errors ({migrationReport?.errors?.length || 0}):
                                </Text>
                                <VStack align="stretch" spacing={1}>
                                  {migrationReport?.errors?.slice(0, 10).map((error, idx) => (
                                    <Text key={idx} fontSize="xs" color="red.500">
                                      • {error}
                                    </Text>
                                  ))}
                                  {(migrationReport?.errors?.length || 0) > 10 && (
                                    <Text fontSize="xs" color="gray.500">
                                      ... and {(migrationReport?.errors?.length || 0) - 10} more errors
                                    </Text>
                                  )}
                                </VStack>
                              </Box>
                            )}

                            <Text fontSize="xs" color="gray.500">
                              Executed by: {migrationReport?.executedBy || 'Unknown'}<br />
                              Executed at: {migrationReport?.executedAt ? new Date(migrationReport?.executedAt).toLocaleString() : 'Unknown'}
                            </Text>
                          </VStack>
                        </Box>
                      )}

                      {/* Documentation */}
                      <Box borderWidth="1px" borderRadius="lg" p={6}>
                        <VStack align="stretch" spacing={3}>
                          <Heading size="sm">What This Tool Fixes</Heading>
                          
                          <Box>
                            <Text fontWeight="bold" fontSize="sm">1. Schema Fracture (estCloseDate → estimatedClosingDate)</Text>
                            <Text fontSize="sm" color="gray.600">
                              Early versions of the CRM used <Kbd>estCloseDate</Kbd> while newer versions use <Kbd>estimatedClosingDate</Kbd>. This creates inconsistencies where some deals have one field and some have the other. The remediation migrates all legacy records to the new standard field and deletes the old one.
                            </Text>
                          </Box>

                          <Box>
                            <Text fontWeight="bold" fontSize="sm">2. Email/Phone Normalization</Text>
                            <Text fontSize="sm" color="gray.600">
                              Contacts created through different entry points (manual, import, webhook) may have inconsistent formatting:
                              <br />• Emails: <Kbd>John@Example.COM</Kbd> vs <Kbd>john@example.com</Kbd>
                              <br />• Phones: <Kbd>(904) 555-1234</Kbd> vs <Kbd>9045551234</Kbd>
                              <br />
                              <br />This causes duplicate detection to fail. The remediation normalizes all emails to lowercase and all phones to 10-digit format.
                            </Text>
                          </Box>

                          <Alert status="warning" borderRadius="md" mt={2}>
                            <AlertIcon />
                            <AlertDescription fontSize="sm">
                              <strong>Important:</strong> Run this remediation ONCE after deploying the backend data normalization updates. After that, all new data will be automatically normalized on creation, and this tool won't be needed again unless you restore old backups.
                            </AlertDescription>
                          </Alert>
                        </VStack>
                      </Box>

                      <Divider />

                      {/* Origin CRM Migration Tool */}
                      <Box>
                        <VStack align="stretch" spacing={6}>
                          <VStack align="flex-start" spacing={0}>
                            <Heading size="md">Origin CRM Migration</Heading>
                            <Text fontSize="sm" color="gray.600">
                              One-time migration to transform existing CRM data to Origin CRM schema
                            </Text>
                          </VStack>

                          <Alert status="warning" borderRadius="md">
                            <AlertIcon />
                            <VStack align="flex-start" spacing={1} flex={1}>
                              <Text fontSize="sm" fontWeight="semibold">
                                ⚠️ Important: Run Dry Run First!
                              </Text>
                              <Text fontSize="xs">
                                Always run the dry run first to preview what will be changed. 
                                Review the report carefully before running the actual migration.
                              </Text>
                            </VStack>
                          </Alert>

                          <Box p={4} bg="blue.50" borderRadius="md" borderWidth="1px" borderColor="blue.200">
                            <VStack align="stretch" spacing={3}>
                              <Text fontSize="sm" fontWeight="semibold">
                                What This Migration Does:
                              </Text>
                              <VStack align="flex-start" spacing={1} fontSize="xs">
                                <Text>✅ Updates all contacts with new schema (contactTypes array, stats object)</Text>
                                <Text>✅ Converts deal buyerAgent/sellerAgent to relatedParties array</Text>
                                <Text>✅ Creates contact records for agents from nested objects</Text>
                                <Text>✅ Computes initial contact stats</Text>
                                <Text>✅ Initializes config/crmSettings with defaults</Text>
                              </VStack>
                            </VStack>
                          </Box>

                          <HStack spacing={4}>
                            <Button
                              leftIcon={<Icon as={FiAlertCircle} />}
                              colorScheme="orange"
                              size="lg"
                              onClick={async () => {
                                setMigrationRunning(true);
                                setMigrationReport(null);
                                setShowMigrationReport(false);

                                try {
                                  const functions = getFunctions(app, 'us-east1');
                                  const migrate = httpsCallable(functions, 'migrateToFusionV1', {
                                    timeout: 600000, // 10 minute timeout
                                  });

                                  toast({
                                    title: 'Running Migration (Dry Run)...',
                                    description: 'This may take a few minutes. Do not close this page.',
                                    status: 'info',
                                    duration: 5000,
                                  });

                                  const result = await migrate({ dryRun: true });

                                  setMigrationReport(result.data.report);
                                  setShowMigrationReport(true);

                                  toast({
                                    title: 'Dry Run Complete!',
                                    description: 'Review the report below. No data was changed.',
                                    status: 'success',
                                    duration: 5000,
                                  });
                                } catch (error) {
                                  console.error('Migration dry run error:', error);
                                  toast({
                                    title: 'Migration Error',
                                    description: error.message,
                                    status: 'error',
                                    duration: 10000,
                                    isClosable: true,
                                  });
                                } finally {
                                  setMigrationRunning(false);
                                }
                              }}
                              isLoading={migrationRunning}
                              loadingText="Running Dry Run..."
                              width="full"
                            >
                              Run Migration (Dry Run)
                            </Button>

                            <Button
                              leftIcon={<Icon as={FiCheckCircle} />}
                              colorScheme="green"
                              size="lg"
                              onClick={async () => {
                                if (!window.confirm('⚠️ IMPORTANT: Have you reviewed the dry run report?\n\nThis will ACTUALLY MODIFY your data. Make sure you:\n1. Ran the dry run\n2. Reviewed the report\n3. Have a recent backup\n\nProceed with actual migration?')) {
                                  return;
                                }

                                setMigrationRunning(true);
                                setMigrationReport(null);
                                setShowMigrationReport(false);

                                try {
                                  const functions = getFunctions(app, 'us-east1');
                                  const migrate = httpsCallable(functions, 'migrateToFusionV1', {
                                    timeout: 600000, // 10 minute timeout
                                  });

                                  toast({
                                    title: 'Running Actual Migration...',
                                    description: 'This will modify your data. Please wait, do not close this page.',
                                    status: 'warning',
                                    duration: 5000,
                                  });

                                  const result = await migrate({ dryRun: false });

                                  setMigrationReport(result.data.report);
                                  setShowMigrationReport(true);

                                  toast({
                                    title: '✅ Migration Complete!',
                                    description: 'Your CRM has been upgraded to Origin CRM. Review the report below.',
                                    status: 'success',
                                    duration: 10000,
                                    isClosable: true,
                                  });
                                } catch (error) {
                                  console.error('Migration error:', error);
                                  toast({
                                    title: 'Migration Failed',
                                    description: error.message,
                                    status: 'error',
                                    duration: 10000,
                                    isClosable: true,
                                  });
                                } finally {
                                  setMigrationRunning(false);
                                }
                              }}
                              isLoading={migrationRunning}
                              loadingText="Running Migration..."
                              width="full"
                              isDisabled={!migrationReport || migrationReport?.dryRun === false}
                            >
                              Run Actual Migration
                            </Button>
                          </HStack>

                          {/* Migration Report */}
                          {showMigrationReport && migrationReport && (
                            <ScaleFade in>
                              <Box
                                p={6}
                                bg={migrationReport?.dryRun ? 'yellow.50' : 'green.50'}
                                borderRadius="lg"
                                borderWidth="2px"
                                borderColor={migrationReport?.dryRun ? 'yellow.300' : 'green.300'}
                              >
                                <VStack align="stretch" spacing={4}>
                                  <HStack justify="space-between">
                                    <Heading size="sm">
                                      {migrationReport?.dryRun ? '🔍 Dry Run Report' : '✅ Migration Report'}
                                    </Heading>
                                    <Badge colorScheme={migrationReport?.dryRun ? 'yellow' : 'green'} fontSize="md">
                                      {migrationReport?.dryRun ? 'Dry Run (No Changes Made)' : 'Completed'}
                                    </Badge>
                                  </HStack>

                                  {/* Contacts Stats */}
                                  <Box p={4} bg="white" borderRadius="md">
                                    <Heading size="xs" mb={3}>📇 Contacts Migration</Heading>
                                    <Grid templateColumns="repeat(3, 1fr)" gap={3}>
                                      <Box>
                                        <Text fontSize="xs" fontWeight="semibold" color="gray.600">Total Contacts</Text>
                                        <Text fontSize="2xl" fontWeight="bold">{migrationReport?.contacts?.total || 0}</Text>
                                      </Box>
                                      <Box>
                                        <Text fontSize="xs" fontWeight="semibold" color="gray.600">Migrated</Text>
                                        <Text fontSize="2xl" fontWeight="bold" color="green.600">
                                          {migrationReport?.contacts?.migrated || 0}
                                        </Text>
                                      </Box>
                                      <Box>
                                        <Text fontSize="xs" fontWeight="semibold" color="gray.600">Errors</Text>
                                        <Text fontSize="2xl" fontWeight="bold" color={(migrationReport?.contacts?.errors?.length || 0) > 0 ? 'red.600' : 'gray.400'}>
                                          {(migrationReport?.contacts?.errors?.length || 0) || 0}
                                        </Text>
                                      </Box>
                                    </Grid>
                                    {(migrationReport?.contacts?.errors?.length || 0) > 0 && (
                                      <Box mt={3} p={2} bg="red.50" borderRadius="md">
                                        <Text fontSize="xs" fontWeight="semibold" color="red.700" mb={1}>Errors:</Text>
                                        <VStack align="stretch" spacing={1}>
                                          {migrationReport?.contacts?.errors.slice(0, 5).map((err, idx) => (
                                            <Text key={idx} fontSize="xs" color="red.600">
                                              {err.contactId}: {err.error}
                                            </Text>
                                          ))}
                                          {migrationReport?.contacts?.errors.length > 5 && (
                                            <Text fontSize="xs" color="red.500">
                                              ...and {migrationReport?.contacts?.errors.length - 5} more errors
                                            </Text>
                                          )}
                                        </VStack>
                                      </Box>
                                    )}
                                  </Box>

                                  {/* Deals Stats */}
                                  <Box p={4} bg="white" borderRadius="md">
                                    <Heading size="xs" mb={3}>💼 Deals Migration</Heading>
                                    <Grid templateColumns="repeat(4, 1fr)" gap={3}>
                                      <Box>
                                        <Text fontSize="xs" fontWeight="semibold" color="gray.600">Total Deals</Text>
                                        <Text fontSize="2xl" fontWeight="bold">{migrationReport?.deals?.total || 0}</Text>
                                      </Box>
                                      <Box>
                                        <Text fontSize="xs" fontWeight="semibold" color="gray.600">Migrated</Text>
                                        <Text fontSize="2xl" fontWeight="bold" color="green.600">
                                          {migrationReport?.deals?.migrated || 0}
                                        </Text>
                                      </Box>
                                      <Box>
                                        <Text fontSize="xs" fontWeight="semibold" color="gray.600">Agents Created</Text>
                                        <Text fontSize="2xl" fontWeight="bold" color="blue.600">
                                          {migrationReport?.deals?.agentsCreated || 0}
                                        </Text>
                                      </Box>
                                      <Box>
                                        <Text fontSize="xs" fontWeight="semibold" color="gray.600">Errors</Text>
                                        <Text fontSize="2xl" fontWeight="bold" color={migrationReport?.deals?.errors.length > 0 ? 'red.600' : 'gray.400'}>
                                          {migrationReport?.deals?.errors.length}
                                        </Text>
                                      </Box>
                                    </Grid>
                                    {migrationReport?.deals?.errors.length > 0 && (
                                      <Box mt={3} p={2} bg="red.50" borderRadius="md">
                                        <Text fontSize="xs" fontWeight="semibold" color="red.700" mb={1}>Errors:</Text>
                                        <VStack align="stretch" spacing={1}>
                                          {migrationReport?.deals?.errors.slice(0, 5).map((err, idx) => (
                                            <Text key={idx} fontSize="xs" color="red.600">
                                              {err.dealId}: {err.error}
                                            </Text>
                                          ))}
                                          {migrationReport?.deals?.errors.length > 5 && (
                                            <Text fontSize="xs" color="red.500">
                                              ...and {migrationReport?.deals?.errors.length - 5} more errors
                                            </Text>
                                          )}
                                        </VStack>
                                      </Box>
                                    )}
                                  </Box>

                                  {/* Config Stats */}
                                  <Box p={4} bg="white" borderRadius="md">
                                    <Heading size="xs" mb={3}>⚙️ Configuration</Heading>
                                    <HStack spacing={6}>
                                      <Box>
                                        <Text fontSize="xs" fontWeight="semibold" color="gray.600">CRM Settings</Text>
                                        <Badge colorScheme={migrationReport?.config?.created ? 'green' : 'gray'}>
                                          {migrationReport?.config?.created ? 'Initialized' : 'Already Exists'}
                                        </Badge>
                                      </Box>
                                    </HStack>
                                  </Box>

                                  {/* Timing */}
                                  <HStack justify="space-between" fontSize="xs" color="gray.600">
                                    <Text>Started: {migrationReport?.startTime ? new Date(migrationReport.startTime).toLocaleString() : 'Unknown'}</Text>
                                    <Text>Ended: {migrationReport?.endTime ? new Date(migrationReport.endTime).toLocaleString() : 'Unknown'}</Text>
                                  </HStack>

                                  {migrationReport?.dryRun && (
                                    <Alert status="success" borderRadius="md">
                                      <AlertIcon />
                                      <VStack align="flex-start" spacing={1} flex={1}>
                                        <Text fontSize="sm" fontWeight="semibold">
                                          ✅ Dry Run Successful
                                        </Text>
                                        <Text fontSize="xs">
                                          Review the statistics above. If everything looks good, click "Run Actual Migration" to apply changes.
                                          {((migrationReport?.contacts?.errors?.length || 0) || 0) === 0 && ((migrationReport?.deals?.errors?.length || 0) || 0) === 0 && (
                                            <Text as="span" color="green.700" fontWeight="bold"> No errors detected!</Text>
                                          )}
                                        </Text>
                                      </VStack>
                                    </Alert>
                                  )}

                                  {!migrationReport?.dryRun && (
                                    <Alert status="success" borderRadius="md">
                                      <AlertIcon />
                                      <VStack align="flex-start" spacing={1} flex={1}>
                                        <Text fontSize="sm" fontWeight="semibold">
                                          🎉 Migration Complete!
                                        </Text>
                                        <Text fontSize="xs">
                                          Your CRM has been successfully upgraded to Origin CRM. 
                                          Navigate to <Link color="blue.600" fontWeight="bold" onClick={() => history.push('/crm/dashboard')} cursor="pointer">/crm/dashboard</Link> to see the new contact-centric interface.
                                        </Text>
                                      </VStack>
                                    </Alert>
                                  )}

                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setShowMigrationReport(false);
                                      setMigrationReport(null);
                                    }}
                                  >
                                    Dismiss Report
                                  </Button>
                                </VStack>
                              </Box>
                            </ScaleFade>
                          )}

                          <Alert status="info" size="sm" borderRadius="md">
                            <AlertIcon />
                            <Text fontSize="xs">
                              💡 Tip: This migration only needs to be run once. After successful migration, 
                              you can remove this panel or leave it for reference.
                            </Text>
                          </Alert>
                        </VStack>
                      </Box>

                      <Divider />

                      {/* Repair Contact-Deal Links */}
                      <Box>
                        <VStack align="stretch" spacing={4}>
                          <VStack align="flex-start" spacing={0}>
                            <Heading size="md">Repair Contact-Deal Links</Heading>
                            <Text fontSize="sm" color="gray.600">
                              Comprehensive repair for contact-deal associations and stats
                            </Text>
                          </VStack>

                          <Alert status="info" borderRadius="md">
                            <AlertIcon />
                            <VStack align="flex-start" spacing={1} flex={1}>
                              <Text fontSize="sm" fontWeight="semibold">
                                🔄 Complete Data Integrity Repair
                              </Text>
                              <Text fontSize="xs">
                                This function performs two critical repairs:
                                <br/>1. Finds deals without contacts and creates/links them
                                <br/>2. Recalculates all contact stats (totalDeals, wonDeals, etc.)
                                <br/>Run this if contacts show zero deals or if activeDeals &gt; totalContacts.
                              </Text>
                            </VStack>
                          </Alert>

                          <Box p={4} bg="green.50" borderRadius="md" borderWidth="1px" borderColor="green.200">
                            <VStack align="flex-start" spacing={2}>
                              <Text fontSize="sm" fontWeight="semibold">What this repairs:</Text>
                              <Text fontSize="xs">✅ Missing contactId on deals</Text>
                              <Text fontSize="xs">✅ Invalid contactId (points to deleted contact)</Text>
                              <Text fontSize="xs">✅ Incorrect contact stats</Text>
                              <Text fontSize="xs">✅ Orphaned deals</Text>
                            </VStack>
                          </Box>

                          <HStack spacing={4}>
                            <Button
                              leftIcon={<Icon as={FiAlertCircle} />}
                              colorScheme="green"
                              size="md"
                              onClick={async () => {
                                try {
                                  const functions = getFunctions(app, 'us-east1');
                                  const repairFunc = httpsCallable(functions, 'repairContactDealLinks', { timeout: 300000 });

                                  toast({
                                    title: 'Running Dry Run...',
                                    description: 'Analyzing data integrity',
                                    status: 'info',
                                    duration: 3000,
                                  });

                                  const result = await repairFunc({ dryRun: true });

                                  toast({
                                    title: 'Dry Run Complete!',
                                    description: 'Check console for detailed report',
                                    status: 'success',
                                    duration: 5000,
                                  });

                                  console.log('Repair Report:', result.data.report);
                                  const report = result.data.report;
                                  alert(`DRY RUN REPORT:\n\nDeals Scanned: ${report.dealsScanned}\nDeals w/ Missing Contact: ${report.dealsWithMissingContact}\nDeals w/ Invalid Contact: ${report.dealsWithInvalidContact}\nContacts to Create: ${report.contactsCreated}\nContacts Scanned: ${report.contactsScanned}\nContact Stats to Update: ${report.contactStatsUpdated}\nErrors: ${report.errors.length}\n\nCheck console for full details.`);
                                } catch (error) {
                                  console.error('Dry run error:', error);
                                  toast({
                                    title: 'Dry Run Failed',
                                    description: error.message,
                                    status: 'error',
                                    duration: 5000,
                                  });
                                }
                              }}
                            >
                              Dry Run (Preview Only)
                            </Button>

                            <Button
                              leftIcon={<Icon as={FiCheckCircle} />}
                              colorScheme="red"
                              size="md"
                              onClick={async () => {
                                const confirmed = window.confirm(
                                  '⚠️ WARNING: This will modify your database.\n\n' +
                                  'This repair will:\n' +
                                  '• Create contacts for deals that are missing them\n' +
                                  '• Update all contactId references\n' +
                                  '• Recalculate all contact stats\n\n' +
                                  'Have you run the DRY RUN first?\n\n' +
                                  'Click OK to proceed with LIVE repair.'
                                );

                                if (!confirmed) {
                                  return;
                                }

                                try {
                                  const functions = getFunctions(app, 'us-east1');
                                  const repairFunc = httpsCallable(functions, 'repairContactDealLinks', { timeout: 300000 });

                                  toast({
                                    title: 'Running Repair...',
                                    description: 'This may take several minutes. Do not close this page.',
                                    status: 'warning',
                                    duration: 5000,
                                  });

                                  const result = await repairFunc({ dryRun: false });

                                  toast({
                                    title: '✅ Repair Complete!',
                                    description: 'All contact-deal links have been repaired',
                                    status: 'success',
                                    duration: 10000,
                                    isClosable: true,
                                  });

                                  console.log('Repair Report:', result.data.report);
                                  const report = result.data.report;
                                  alert(`REPAIR COMPLETE!\n\nContacts Created: ${report.contactsCreated}\nDeals Updated: ${report.dealsUpdated}\nContact Stats Updated: ${report.contactStatsUpdated}\nErrors: ${report.errors.length}\n\nCheck console for full report.`);
                                } catch (error) {
                                  console.error('Repair error:', error);
                                  toast({
                                    title: 'Repair Failed',
                                    description: error.message,
                                    status: 'error',
                                    duration: 10000,
                                    isClosable: true,
                                  });
                                }
                              }}
                            >
                              Run Repair (LIVE - WILL APPLY CHANGES)
                            </Button>
                          </HStack>

                          <Text fontSize="xs" color="gray.500">
                            💡 Always run dry run first to preview changes before running the live repair.
                          </Text>
                        </VStack>
                      </Box>

                      <Divider />

                      {/* Enrich Related Parties Migration */}
                      <Box>
                        <VStack align="stretch" spacing={4}>
                          <VStack align="flex-start" spacing={0}>
                            <Heading size="md">Enrich Related Parties</Heading>
                            <Text fontSize="sm" color="gray.600">
                              Fix "Unknown Contact" in related parties by adding contact info
                            </Text>
                          </VStack>

                          <Alert status="info" borderRadius="md">
                            <AlertIcon />
                            <VStack align="flex-start" spacing={1} flex={1}>
                              <Text fontSize="sm" fontWeight="semibold">
                                What This Does
                              </Text>
                              <Text fontSize="xs">
                                Updates all existing related parties to include contactName, contactEmail, and contactPhone.
                                This fixes the "Unknown Contact" display issue for parties added before this update.
                              </Text>
                            </VStack>
                          </Alert>

                          <Button
                            leftIcon={<Icon as={FiCheckCircle} />}
                            colorScheme="blue"
                            size="md"
                            onClick={async () => {
                              if (!window.confirm('Run migration to enrich all related parties with contact info? This is safe to run and will improve display.')) {
                                return;
                              }

                              try {
                                const functions = getFunctions(app, 'us-east1');
                                const enrichFunc = httpsCallable(functions, 'enrichRelatedParties', { timeout: 300000 });

                                toast({
                                  title: 'Enriching related parties...',
                                  description: 'This may take a moment',
                                  status: 'info',
                                  duration: 3000,
                                });

                                const result = await enrichFunc({ dryRun: false });

                                toast({
                                  title: 'Migration Complete!',
                                  description: result.data.message,
                                  status: 'success',
                                  duration: 5000,
                                });
                              } catch (error) {
                                console.error('Migration error:', error);
                                toast({
                                  title: 'Migration Failed',
                                  description: error.message,
                                  status: 'error',
                                  duration: 5000,
                                });
                              }
                            }}
                          >
                            Enrich All Related Parties
                          </Button>

                          <Text fontSize="xs" color="gray.500">
                            💡 This is safe to run multiple times. It will only update parties that need enrichment.
                          </Text>
                        </VStack>
                      </Box>

                      <Divider />

                      {/* Associate Deals with Contacts */}
                      <Box>
                        <VStack align="stretch" spacing={4}>
                          <VStack align="flex-start" spacing={0}>
                            <Heading size="md">Associate Deals with Contacts</Heading>
                            <Text fontSize="sm" color="gray.600">
                              Fix orphaned deals and ensure proper deal-contact associations
                            </Text>
                          </VStack>

                          <Alert status="warning" borderRadius="md">
                            <AlertIcon />
                            <VStack align="flex-start" spacing={1} flex={1}>
                              <Text fontSize="sm" fontWeight="semibold">
                                🔧 Critical Database Maintenance
                              </Text>
                              <Text fontSize="xs">
                                This function searches all deals and ensures each one has a proper contact association.
                                It's scoped per user to prevent cross-contamination. Run dry run first to see what will change.
                              </Text>
                            </VStack>
                          </Alert>

                          <Box p={4} bg="blue.50" borderRadius="md" borderWidth="1px" borderColor="blue.200">
                            <VStack align="stretch" spacing={2}>
                              <Text fontSize="sm" fontWeight="semibold">What This Does:</Text>
                              <VStack align="flex-start" spacing={1} fontSize="xs">
                                <Text>✅ Finds deals without contactId</Text>
                                <Text>✅ Searches for matching contact by email/phone (within same owner)</Text>
                                <Text>✅ Creates contact if not found (prevents cross-user contamination)</Text>
                                <Text>✅ Associates deal with contact</Text>
                                <Text>✅ Syncs contact/deal ownership to prevent disappearing contacts</Text>
                                <Text>✅ Fixes "No deals" showing in Contact Detail Drawer</Text>
                              </VStack>
                            </VStack>
                          </Box>

                          <HStack spacing={4}>
                            <Button
                              leftIcon={<Icon as={FiAlertCircle} />}
                              colorScheme="orange"
                              size="md"
                              onClick={async () => {
                                try {
                                  const functions = getFunctions(app, 'us-east1');
                                  const associateFunc = httpsCallable(functions, 'associateDealsWithContacts', { timeout: 300000 });

                                  toast({
                                    title: 'Running Dry Run...',
                                    description: 'Analyzing deals and contacts',
                                    status: 'info',
                                    duration: 3000,
                                  });

                                  const result = await associateFunc({ dryRun: true });

                                  toast({
                                    title: 'Dry Run Complete!',
                                    description: result.data.message,
                                    status: 'success',
                                    duration: 5000,
                                  });

                                  console.log('Association Report:', result.data.report);
                                  alert(`DRY RUN REPORT:\n\nDeals Processed: ${result.data.report.dealsProcessed}\nDeals Fixed: ${result.data.report.dealsFixed}\nContacts Created: ${result.data.report.contactsCreated}\nAlready Valid: ${result.data.report.dealsAlreadyValid}\nErrors: ${result.data.report.errors.length}\n\nCheck console for full report.`);
                                } catch (error) {
                                  console.error('Dry run error:', error);
                                  toast({
                                    title: 'Dry Run Failed',
                                    description: error.message,
                                    status: 'error',
                                    duration: 5000,
                                  });
                                }
                              }}
                            >
                              Run Dry Run
                            </Button>

                            <Button
                              leftIcon={<Icon as={FiCheckCircle} />}
                              colorScheme="green"
                              size="md"
                              onClick={async () => {
                                if (!window.confirm('⚠️ IMPORTANT: Have you reviewed the dry run results?\n\nThis will modify your database by:\n1. Creating missing contacts\n2. Updating deal contactIds\n3. Syncing contact/deal ownership\n\nProceed with actual migration?')) {
                                  return;
                                }

                                try {
                                  const functions = getFunctions(app, 'us-east1');
                                  const associateFunc = httpsCallable(functions, 'associateDealsWithContacts', { timeout: 300000 });

                                  toast({
                                    title: 'Running Migration...',
                                    description: 'This may take several minutes. Do not close this page.',
                                    status: 'warning',
                                    duration: 5000,
                                  });

                                  const result = await associateFunc({ dryRun: false });

                                  toast({
                                    title: '✅ Migration Complete!',
                                    description: result.data.message,
                                    status: 'success',
                                    duration: 10000,
                                    isClosable: true,
                                  });

                                  console.log('Association Report:', result.data.report);
                                  alert(`MIGRATION COMPLETE!\n\nDeals Fixed: ${result.data.report.dealsFixed}\nContacts Created: ${result.data.report.contactsCreated}\nErrors: ${result.data.report.errors.length}\n\nCheck console for full report.`);
                                } catch (error) {
                                  console.error('Migration error:', error);
                                  toast({
                                    title: 'Migration Failed',
                                    description: error.message,
                                    status: 'error',
                                    duration: 10000,
                                    isClosable: true,
                                  });
                                }
                              }}
                            >
                              Run Actual Migration
                            </Button>
                          </HStack>

                          <Text fontSize="xs" color="gray.500">
                            💡 Safe to run multiple times. Only updates deals that need fixes.
                          </Text>
                        </VStack>
                      </Box>

                      <Divider />

                      {/* Migrate Deal Participants - Performance Fix */}
                      <Box>
                        <VStack align="stretch" spacing={4}>
                          <VStack align="flex-start" spacing={0}>
                            <Heading size="md">Migrate Deal Participants</Heading>
                            <Badge colorScheme="purple" fontSize="xs" mb={2}>PERFORMANCE OPTIMIZATION</Badge>
                            <Text fontSize="sm" color="gray.600">
                              Create participants subcollection for efficient querying
                            </Text>
                          </VStack>

                          <Alert status="info" borderRadius="md">
                            <AlertIcon />
                            <VStack align="flex-start" spacing={1} flex={1}>
                              <Text fontSize="sm" fontWeight="semibold">
                                🚀 Critical Performance Fix
                              </Text>
                              <Text fontSize="xs">
                                This migration creates a `participants` subcollection from existing `relatedParties` arrays.
                                This enables efficient O(1) queries instead of O(n), dramatically improving performance
                                when viewing contact stats and related party deals.
                              </Text>
                            </VStack>
                          </Alert>

                          <Box p={4} bg="purple.50" borderRadius="md" borderWidth="1px" borderColor="purple.200">
                            <VStack align="stretch" spacing={2}>
                              <Text fontSize="sm" fontWeight="semibold">Performance Impact:</Text>
                              <VStack align="flex-start" spacing={1} fontSize="xs">
                                <Text>📉 <strong>BEFORE:</strong> Fetching 1,000 deals to find related parties (1,000+ reads)</Text>
                                <Text>📈 <strong>AFTER:</strong> Direct query to participants (~5 reads)</Text>
                                <Text>⚡ <strong>Result:</strong> 200x faster contact stats updates</Text>
                              </VStack>
                              <Divider my={2} />
                              <Text fontSize="sm" fontWeight="semibold">What This Does:</Text>
                              <VStack align="flex-start" spacing={1} fontSize="xs">
                                <Text>✅ Creates deals/{'dealId'}/participants/{'contactId'} docs</Text>
                                <Text>✅ Copies data from relatedParties array</Text>
                                <Text>✅ Preserves ownerId for data isolation</Text>
                                <Text>✅ Safe to run multiple times (idempotent)</Text>
                                <Text>✅ Does NOT delete relatedParties (keeps backward compatibility)</Text>
                              </VStack>
                            </VStack>
                          </Box>

                          <HStack spacing={4}>
                            <Button
                              leftIcon={<Icon as={FiAlertCircle} />}
                              colorScheme="blue"
                              size="md"
                              onClick={async () => {
                                try {
                                  const functions = getFunctions(app, 'us-east1');
                                  const migrateFunc = httpsCallable(functions, 'migrateDealParticipants', { timeout: 540000 });

                                  toast({
                                    title: 'Running Dry Run...',
                                    description: 'Analyzing deals and participants (no changes will be made)',
                                    status: 'info',
                                    duration: 3000,
                                  });

                                  const result = await migrateFunc({ dryRun: true });

                                  console.log('Participants Migration Dry Run:', result.data);

                                  toast({
                                    title: 'Dry Run Complete!',
                                    description: result.data.message,
                                    status: 'success',
                                    duration: 10000,
                                    isClosable: true,
                                  });

                                  const stats = result.data.stats;
                                  alert(`DRY RUN COMPLETE!\n\nDeals: ${stats.dealsProcessed}\nParticipants to Create: ${stats.participantsCreated}\n\nNo changes were made. Review and run actual migration if ready.`);
                                } catch (error) {
                                  console.error('Migration error:', error);
                                  toast({
                                    title: 'Dry Run Failed',
                                    description: error.message,
                                    status: 'error',
                                    duration: 10000,
                                    isClosable: true,
                                  });
                                }
                              }}
                            >
                              Run Dry Run
                            </Button>

                            <Button
                              leftIcon={<Icon as={FiCheckCircle} />}
                              colorScheme="green"
                              size="md"
                              onClick={async () => {
                                if (!window.confirm('⚠️ IMPORTANT:\n\nThis will create participant documents for all deals with related parties.\n\nThis is REQUIRED for the performance improvements to work.\n\nProceed with migration?')) {
                                  return;
                                }

                                try {
                                  const functions = getFunctions(app, 'us-east1');
                                  const migrateFunc = httpsCallable(functions, 'migrateDealParticipants', { timeout: 540000 });

                                  toast({
                                    title: 'Running Migration...',
                                    description: 'Creating participant documents. This may take several minutes.',
                                    status: 'warning',
                                    duration: 5000,
                                  });

                                  const result = await migrateFunc({ dryRun: false });

                                  toast({
                                    title: '✅ Migration Complete!',
                                    description: result.data.message,
                                    status: 'success',
                                    duration: 10000,
                                    isClosable: true,
                                  });

                                  console.log('Participants Migration Report:', result.data.stats);
                                  const stats = result.data.stats;
                                  alert(`MIGRATION COMPLETE!\n\nDeals Processed: ${stats.dealsProcessed}\nParticipants Created: ${stats.participantsCreated}\nParticipants Skipped (already exist): ${stats.participantsSkipped}\nErrors: ${stats.errors.length}\n\nCheck console for full report.`);
                                } catch (error) {
                                  console.error('Migration error:', error);
                                  toast({
                                    title: 'Migration Failed',
                                    description: error.message,
                                    status: 'error',
                                    duration: 10000,
                                    isClosable: true,
                                  });
                                }
                              }}
                            >
                              Run Migration (LIVE)
                            </Button>

                            <Button
                              leftIcon={<Icon as={FiCheckCircle} />}
                              colorScheme="purple"
                              variant="outline"
                              size="md"
                              onClick={async () => {
                                try {
                                  const functions = getFunctions(app, 'us-east1');
                                  const verifyFunc = httpsCallable(functions, 'verifyDealParticipantsMigration', { timeout: 300000 });

                                  toast({
                                    title: 'Verifying Migration...',
                                    description: 'Checking participants vs relatedParties counts',
                                    status: 'info',
                                    duration: 3000,
                                  });

                                  const result = await verifyFunc();

                                  console.log('Verification Result:', result.data);

                                  const status = result.data.valid ? 'success' : 'warning';
                                  toast({
                                    title: result.data.valid ? '✅ Verification Passed!' : '⚠️ Mismatches Found',
                                    description: result.data.message,
                                    status,
                                    duration: 10000,
                                    isClosable: true,
                                  });

                                  alert(`VERIFICATION RESULT:\n\n${result.data.message}\n\nTotal Deals: ${result.data.totalDeals}\nRelated Parties: ${result.data.totalRelatedParties}\nParticipants: ${result.data.totalParticipants}\nMismatches: ${result.data.mismatches.length}\n\nCheck console for details.`);
                                } catch (error) {
                                  console.error('Verification error:', error);
                                  toast({
                                    title: 'Verification Failed',
                                    description: error.message,
                                    status: 'error',
                                    duration: 10000,
                                    isClosable: true,
                                  });
                                }
                              }}
                            >
                              Verify Migration
                            </Button>
                          </HStack>

                          <Text fontSize="xs" color="gray.500">
                            💡 Run this migration ONCE after deploying the backend changes. Then the performance improvements will take effect automatically.
                          </Text>
                        </VStack>
                      </Box>
                    </VStack>
                  </Box>
                </Fade>
              </TabPanel>

              {/* GMAIL SIGNATURE TEST TAB */}
              <TabPanel px={0}>
                <Fade in>
                  <Box bg={boxBG} borderRadius="xl" p={6} shadow="sm">
                    <VStack align="stretch" spacing={6}>
                      <VStack align="flex-start" spacing={0}>
                        <Heading size="lg">Gmail Signature & Email Testing</Heading>
                        <Text fontSize="sm" color="gray.600">
                          Test Gmail API integration, signature fetching, and email sending with Domain-Wide Delegation
                        </Text>
                      </VStack>

                      <Alert status="info" borderRadius="md">
                        <AlertIcon />
                        <VStack align="flex-start" spacing={1} flex={1}>
                          <Text fontSize="sm" fontWeight="semibold">
                            How This Works
                          </Text>
                          <Text fontSize="xs">
                            This uses Service Account with Domain-Wide Delegation to impersonate users and access their Gmail settings.
                            You can test as yourself (leave blank) or as any other user by entering their email.
                          </Text>
                        </VStack>
                      </Alert>

                      <Divider />

                      {/* User Selection */}
                      <Box>
                        <Heading size="sm" mb={4}>Test Configuration</Heading>
                        <FormControl>
                          <FormLabel fontSize="sm">Test as User (optional)</FormLabel>
                          <Input
                            value={testUserEmail}
                            onChange={(e) => setTestUserEmail(e.target.value)}
                            placeholder="Leave blank to use your own email"
                            size="md"
                          />
                          <Text fontSize="xs" color="gray.500" mt={1}>
                            Enter any user's email from your organization (e.g., mitchellm@future.loans)
                          </Text>
                        </FormControl>
                      </Box>

                      <Divider />

                      {/* Signature Test */}
                      <Box>
                        <Heading size="sm" mb={4}>Test 1: Fetch Gmail Signature</Heading>
                        <Text fontSize="sm" color="gray.600" mb={4}>
                          Retrieve the Gmail signature for the specified user to verify Service Account access.
                        </Text>
                        
                        <VStack align="stretch" spacing={3}>
                          <Button
                            leftIcon={<Icon as={FiCheckCircle} />}
                            colorScheme="purple"
                            variant="outline"
                            onClick={handleTestSignature}
                            isLoading={isTestingSignature}
                            loadingText="Fetching signature..."
                            size="md"
                          >
                            Test Fetch Signature
                          </Button>
                          
                          {signatureResult && (
                            <Box 
                              p={4} 
                              bg={signatureResult.error ? 'red.50' : 'green.50'} 
                              borderRadius="md" 
                              borderWidth="1px" 
                              borderColor={signatureResult.error ? 'red.200' : 'green.200'}
                            >
                              {signatureResult.error ? (
                                <VStack align="flex-start" spacing={2}>
                                  <HStack>
                                    <Icon as={FiXCircle} color="red.500" />
                                    <Text fontWeight="semibold" fontSize="sm" color="red.700">
                                      Error
                                    </Text>
                                  </HStack>
                                  <Box 
                                    fontSize="xs" 
                                    p={3} 
                                    bg="white" 
                                    borderRadius="md" 
                                    w="full"
                                    fontFamily="monospace"
                                  >
                                    {signatureResult.error}
                                  </Box>
                                </VStack>
                              ) : (
                                <VStack align="flex-start" spacing={2}>
                                  <HStack>
                                    <Icon as={FiCheckCircle} color="green.500" />
                                    <Text fontWeight="semibold" fontSize="sm" color="green.700">
                                      {signatureResult.hasSignature ? 'Signature Found' : 'No Signature'}
                                    </Text>
                                  </HStack>
                                  {signatureResult.hasSignature && (
                                    <>
                                      <Text fontSize="xs" color="gray.600">
                                        Length: {signatureResult.signature.length} characters
                                      </Text>
                                      <Box 
                                        maxH="200px" 
                                        overflowY="auto" 
                                        p={3} 
                                        bg="white" 
                                        borderRadius="md" 
                                        borderWidth="1px"
                                        w="full"
                                      >
                                        <Box dangerouslySetInnerHTML={{ __html: signatureResult.signature }} />
                                      </Box>
                                    </>
                                  )}
                                </VStack>
                              )}
                            </Box>
                          )}
                        </VStack>
                      </Box>

                      <Divider />

                      {/* Send Test Email */}
                      <Box>
                        <Heading size="sm" mb={4}>Test 2: Send Email with Signature</Heading>
                        <Text fontSize="sm" color="gray.600" mb={4}>
                          Send a test email to verify both signature fetching and email sending work together.
                        </Text>
                        
                        <VStack align="stretch" spacing={3}>
                          <FormControl isRequired>
                            <FormLabel fontSize="sm">Send Test Email To:</FormLabel>
                            <Input
                              value={testEmailTo}
                              onChange={(e) => setTestEmailTo(e.target.value)}
                              placeholder="your-email@example.com"
                              size="md"
                            />
                            <Text fontSize="xs" color="gray.500" mt={1}>
                              Enter your email to receive a test message
                            </Text>
                          </FormControl>

                          <Button
                            leftIcon={<Icon as={FiMail} />}
                            colorScheme="blue"
                            onClick={handleSendTestEmail}
                            isLoading={isSendingTestEmail}
                            loadingText="Sending..."
                            size="md"
                            isDisabled={!testEmailTo || !testEmailTo.trim()}
                          >
                            Send Test Email {testUserEmail ? `as ${testUserEmail}` : ''}
                          </Button>
                        </VStack>
                      </Box>

                      <Divider />

                      {/* Configuration Info */}
                      <Box p={4} bg="blue.50" borderRadius="md" borderWidth="1px" borderColor="blue.200">
                        <VStack align="stretch" spacing={3}>
                          <Text fontSize="sm" fontWeight="semibold">
                            Required Configuration:
                          </Text>
                          <VStack align="flex-start" spacing={1} fontSize="xs">
                            <Text>✅ Gmail API enabled in Google Cloud Console</Text>
                            <Text>✅ Service Account has Domain-Wide Delegation</Text>
                            <Text>✅ Scopes authorized: gmail.settings.basic, gmail.send</Text>
                            <Text>✅ Firebase Functions config has service account credentials</Text>
                          </VStack>
                          <Divider />
                          <Text fontSize="sm" fontWeight="semibold">
                            Current Dev Service Account:
                          </Text>
                          <Text fontSize="xs" fontFamily="mono" color="gray.600">
                            rate-lock-notifications@dev---future-operations-hub.iam.gserviceaccount.com
                          </Text>
                          <Text fontSize="xs" fontFamily="mono" color="gray.600">
                            Client ID: 105719025105016337150
                          </Text>
                        </VStack>
                      </Box>

                      {/* Common Errors */}
                      <Box p={4} bg="orange.50" borderRadius="md" borderWidth="1px" borderColor="orange.200">
                        <VStack align="stretch" spacing={3}>
                          <Text fontSize="sm" fontWeight="semibold" color="orange.700">
                            Common Errors & Solutions:
                          </Text>
                          <VStack align="flex-start" spacing={2} fontSize="xs">
                            <Box>
                              <Text fontWeight="bold">Error: "Gmail API has not been used"</Text>
                              <Text>Solution: Run <code>gcloud services enable gmail.googleapis.com --project=dev---future-operations-hub</code></Text>
                            </Box>
                            <Box>
                              <Text fontWeight="bold">Error: "unauthorized_client"</Text>
                              <Text>Solution: Add Client ID to Domain-Wide Delegation in Google Admin Console</Text>
                            </Box>
                            <Box>
                              <Text fontWeight="bold">Error: "Insufficient authentication scopes"</Text>
                              <Text>Solution: Verify scopes in Domain-Wide Delegation match exactly</Text>
                            </Box>
                            <Box>
                              <Text fontWeight="bold">Error: "User not found"</Text>
                              <Text>Solution: Make sure the user exists in the CRM Users list</Text>
                            </Box>
                          </VStack>
                        </VStack>
                      </Box>
                    </VStack>
                  </Box>
                </Fade>
              </TabPanel>

              {/* HOT LEAD QUEUE TAB */}
              <TabPanel px={0}>
                <Fade in>
                  <Box bg={boxBG} borderRadius="xl" p={6} shadow="sm" data-tour="admin-hotlead">
                    <HotLeadQueueManager 
                      toast={toast} 
                      currentUser={currentUser}
                      crmUsers={crmUsers}
                    />
                  </Box>
                </Fade>
              </TabPanel>

              {/* ARIVE SYNC RULES TAB */}
              <TabPanel px={0}>
                <Fade in>
                  <Box data-tour="admin-arive-rules">
                    <AriveSyncRulesAdmin />
                  </Box>
                </Fade>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </VStack>

        {/* Contact Detail Drawer (opened by clicking an unassigned lead row) */}
        {selectedContactForDrawer && (
          <ContactDetailDrawer
            isOpen={isContactDrawerOpen}
            onClose={() => {
              onContactDrawerClose();
              setSelectedContactForDrawer(null);
            }}
            contact={selectedContactForDrawer}
            contactId={selectedContactForDrawer.id}
            crmUsers={crmUsers}
          />
        )}

        {/* Unassigned Deal Details Modal */}
        <Modal isOpen={isDealDetailsOpen} onClose={onDealDetailsClose} size="xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Lead Details: {selectedUnassignedDeal?.dealName}</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              {selectedUnassignedDeal && (
                <VStack align="stretch" spacing={5}>
                  {/* Source/Tracking Information */}
                  <Box>
                    <Text fontWeight="bold" mb={3} fontSize="md" color="blue.600">📊 Source & Tracking</Text>
                    <SimpleGrid columns={2} spacing={3}>
                      <Box p={3} bg="gray.50" borderRadius="md">
                        <Text fontSize="xs" color="gray.500" fontWeight="medium">Lead Source</Text>
                        <Text fontWeight="semibold">{selectedUnassignedDeal.source || '—'}</Text>
                      </Box>
                      <Box p={3} bg="gray.50" borderRadius="md">
                        <Text fontSize="xs" color="gray.500" fontWeight="medium">Campaign</Text>
                        <Text fontWeight="semibold">{selectedUnassignedDeal.sourceDetails?.campaignId || '—'}</Text>
                      </Box>
                      <Box p={3} bg="gray.50" borderRadius="md">
                        <Text fontSize="xs" color="gray.500" fontWeight="medium">Ad Group</Text>
                        <Text fontWeight="semibold">{selectedUnassignedDeal.sourceDetails?.adGroupId || '—'}</Text>
                      </Box>
                      <Box p={3} bg="gray.50" borderRadius="md">
                        <Text fontSize="xs" color="gray.500" fontWeight="medium">Keyword/Term</Text>
                        <Text fontWeight="semibold">{selectedUnassignedDeal.sourceDetails?.term || '—'}</Text>
                      </Box>
                      <Box p={3} bg="gray.50" borderRadius="md">
                        <Text fontSize="xs" color="gray.500" fontWeight="medium">Medium</Text>
                        <Text fontWeight="semibold">{selectedUnassignedDeal.sourceDetails?.medium || '—'}</Text>
                      </Box>
                      <Box p={3} bg="gray.50" borderRadius="md">
                        <Text fontSize="xs" color="gray.500" fontWeight="medium">Lead Cost</Text>
                        <Text fontWeight="semibold" color={selectedUnassignedDeal.sourceDetails?.leadCost ? 'green.600' : 'gray.400'}>
                          {selectedUnassignedDeal.sourceDetails?.leadCost ? `$${selectedUnassignedDeal.sourceDetails.leadCost}` : '—'}
                        </Text>
                      </Box>
                    </SimpleGrid>
                  </Box>
                  
                  {/* All Lead Information from Webhook */}
                  <Box>
                    <Text fontWeight="bold" mb={3} fontSize="md" color="blue.600">📋 Lead Information</Text>
                    <Box 
                      borderWidth="1px"
                      borderRadius="lg"
                      overflow="hidden"
                    >
                      {formatWebhookData(selectedUnassignedDeal.sourceDetails?.webhookData).length > 0 ? (
                        <Table size="sm" variant="simple">
                          <Tbody>
                            {formatWebhookData(selectedUnassignedDeal.sourceDetails?.webhookData).map(({ label, value, key }) => (
                              <Tr key={key} _hover={{ bg: 'gray.50' }}>
                                <Td 
                                  fontWeight="medium" 
                                  color="gray.600" 
                                  width="40%"
                                  borderColor="gray.100"
                                >
                                  {label}
                                </Td>
                                <Td borderColor="gray.100">
                                  {typeof value === 'string' && value.startsWith('{') ? (
                                    <Text fontSize="xs" fontFamily="mono" color="gray.600">
                                      {value}
                                    </Text>
                                  ) : (
                                    <Text fontWeight="semibold">{value}</Text>
                                  )}
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      ) : (
                        <Box p={4} textAlign="center" color="gray.500">
                          <Text>No additional lead data available</Text>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </VStack>
              )}
            </ModalBody>
            <ModalFooter>
              <Button onClick={onDealDetailsClose}>Close</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Delete Lead Confirmation Dialog */}
        <AlertDialog
          isOpen={isDeleteAlertOpen}
          leastDestructiveRef={deleteAlertCancelRef}
          onClose={() => {
            onDeleteAlertClose();
            setDealToDelete(null);
          }}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                🗑️ Delete Lead
              </AlertDialogHeader>

              <AlertDialogBody>
                <VStack align="stretch" spacing={3}>
                  <Text>
                    Are you sure you want to delete this lead? This action cannot be undone.
                  </Text>
                  {dealToDelete && (
                    <Box p={3} bg="red.50" borderRadius="md" borderWidth="1px" borderColor="red.200">
                      <Text fontWeight="bold" color="red.700">{dealToDelete.dealName}</Text>
                      <Text fontSize="sm" color="red.600">
                        Contact: {dealToDelete.contact?.email || dealToDelete.contact?.phone || 'Unknown'}
                      </Text>
                    </Box>
                  )}
                  <Text fontSize="sm" color="gray.600">
                    This will permanently delete both the deal and the associated contact record.
                  </Text>
                </VStack>
              </AlertDialogBody>

              <AlertDialogFooter>
                <Button 
                  ref={deleteAlertCancelRef} 
                  onClick={() => {
                    onDeleteAlertClose();
                    setDealToDelete(null);
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  colorScheme="red" 
                  onClick={handleDeleteLead} 
                  ml={3}
                  isLoading={isDeleting}
                  loadingText="Deleting..."
                >
                  Delete Lead
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>

        {/* User Edit Modal */}
        <Modal isOpen={isUserModalOpen} onClose={onUserModalClose} size="lg">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              Edit User: {selectedUser?.Name || selectedUser?.name}
            </ModalHeader>
            <ModalCloseButton />
            <Formik
              initialValues={{
                fullName: selectedUser?.Name || selectedUser?.name || '',
                email: selectedUser?.Email || selectedUser?.email || '',
                licensedStates: selectedUser?.['Licensed States'] || [],
              }}
              validationSchema={userValidationSchema}
              onSubmit={handleUserSubmit}
              enableReinitialize
            >
              {({ handleSubmit, values, setFieldValue }) => (
                <Box as="form" onSubmit={handleSubmit}>
                  <ModalBody>
                    <VStack spacing={4} align="stretch">
                      <InputControl name="fullName" label="Full Name" />
                      <InputControl name="email" label="Email" type="email" />
                      
                      <Box>
                        <Text fontSize="sm" fontWeight="medium" mb={2} color="gray.600">
                          CRM Role
                        </Text>
                        <Text fontSize="xs" color="gray.500" mb={2}>
                          Managed on main Admin page (/admin)
                        </Text>
                        <Input
                          value={selectedUser?.CRMRole || 'Not Set'}
                          isReadOnly
                          bg="gray.100"
                          size="sm"
                        />
                      </Box>

                      <Box>
                        <Text fontSize="sm" fontWeight="medium" mb={2} color="gray.600">
                          Branch
                        </Text>
                        <Text fontSize="xs" color="gray.500" mb={2}>
                          Managed on main Admin page (/admin)
                        </Text>
                        <Input
                          value={selectedUser?.Branch || 'Not Set'}
                          isReadOnly
                          bg="gray.100"
                          size="sm"
                        />
                      </Box>

                      <Box>
                        <Text fontSize="sm" fontWeight="medium" mb={2} color="gray.600">
                          NMLS #
                        </Text>
                        <Text fontSize="xs" color="gray.500" mb={2}>
                          Managed on main Admin page (/admin)
                        </Text>
                        <Input
                          value={selectedUser?.NMLS || 'Not Set'}
                          isReadOnly
                          bg="gray.100"
                          size="sm"
                        />
                      </Box>

                      <FormControl>
                        <FormLabel fontSize="sm">Licensed States</FormLabel>
                        
                        {values.licensedStates && values.licensedStates.length > 0 ? (
                          <Wrap spacing={2} mb={3}>
                            {[...values.licensedStates].sort().map((stateCode) => (
                              <WrapItem key={stateCode}>
                                <Tag size="md" colorScheme="blue" borderRadius="full">
                                  <TagLabel>{stateCode}</TagLabel>
                                  <TagCloseButton 
                                    onClick={() => {
                                      const newStates = values.licensedStates.filter(s => s !== stateCode);
                                      setFieldValue('licensedStates', newStates);
                                    }} 
                                  />
                                </Tag>
                              </WrapItem>
                            ))}
                          </Wrap>
                        ) : (
                          <Text fontSize="sm" color="gray.500" mb={3}>
                            No licensed states
                          </Text>
                        )}

                        <Select
                          name="addState"
                          options={stateOptions.filter(option => 
                            !values.licensedStates?.includes(option.value)
                          )}
                          placeholder="Select a state to add..."
                          onChange={(selectedOption) => {
                            if (selectedOption) {
                              const newStates = [...(values.licensedStates || []), selectedOption.value].sort();
                              setFieldValue('licensedStates', newStates);
                            }
                          }}
                          value={null}
                          isClearable={false}
                          size="sm"
                        />
                      </FormControl>
                    </VStack>
                  </ModalBody>
                  <ModalFooter>
                    <Button variant="ghost" mr={3} onClick={onUserModalClose}>
                      Cancel
                    </Button>
                    <SubmitButton colorScheme="blue">Save Changes</SubmitButton>
                  </ModalFooter>
                </Box>
              )}
            </Formik>
          </ModalContent>
        </Modal>
      </Container>
    </Box>
  );
}

// CrmSettingsProvider is at the app root (App.jsx) — no need to wrap again here
export default function AdminPage() {
  return (
    <AdminPageContent />
  );
}
